/* eslint-disable */
import RegisterPage from './pages/Register.jsx';
import TradingTerminal from './TradingTerminal.jsx';
import { TermsPage, RiskPage, PrivacyPage, RefundPage, KYCAMLPage } from './pages/LegalPages.jsx';
import { useState, useEffect, useRef } from 'react';
import { auth, accounts, trades as tradesApi, payments } from './lib/api';
import { useMarketData } from './lib/useMarketData';
// ============================================================
// CLEARFUND TRADING PLATFORM — PHASE 1 COMPLETE BUILD
// Covers: Marketing site, Auth, Dashboard, Rule Engine,
//         Payout System, Admin Portal, SIM Trading UI
// ============================================================

// ─── GLOBAL STYLES ───────────────────────────────────────────
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Mono:ital,wght@0,300;0,400;0,500;1,400&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,400&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    :root {
      --bg: #080A0F;
      --bg2: #0D1117;
      --bg3: #131820;
      --surface: #161D27;
      --surface2: #1C2535;
      --border: rgba(255,255,255,0.07);
      --border2: rgba(255,255,255,0.12);
      --green: #00E5A0;
      --green-dim: rgba(0,229,160,0.12);
      --green-glow: rgba(0,229,160,0.25);
      --red: #FF4757;
      --red-dim: rgba(255,71,87,0.12);
      --amber: #FFA502;
      --amber-dim: rgba(255,165,2,0.12);
      --blue: #3D8BFF;
      --blue-dim: rgba(61,139,255,0.12);
      --text: #E8EDF5;
      --text2: #8A96A8;
      --text3: #5A6478;
      --font-display: 'Syne', sans-serif;
      --font-body: 'DM Sans', sans-serif;
      --font-mono: 'DM Mono', monospace;
      --radius: 8px;
      --radius-lg: 14px;
      --transition: 0.18s ease;
    }
    
    html, body, #root { height: 100%; }
    body {
      background: var(--bg);
      color: var(--text);
      font-family: var(--font-body);
      font-size: 15px;
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }
    
    /* Scrollbar */
    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: var(--bg2); }
    ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }
    
    /* Animations */
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes glow { 0%,100% { box-shadow: 0 0 20px var(--green-glow); } 50% { box-shadow: 0 0 40px var(--green-glow); } }
    @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
    @keyframes barGrow { from { width: 0; } to { width: var(--w); } }
    @keyframes candleFlash { 0%,100% { opacity:1; } 50% { opacity:0.6; } }
    
    .fade-in { animation: fadeIn 0.3s ease forwards; }
    
    button { cursor: pointer; font-family: var(--font-body); }
    input, select, textarea {
      font-family: var(--font-body);
      background: var(--surface);
      border: 1px solid var(--border);
      color: var(--text);
      border-radius: var(--radius);
      padding: 10px 14px;
      font-size: 14px;
      outline: none;
      width: 100%;
      transition: border-color var(--transition);
    }
    input:focus, select:focus, textarea:focus {
      border-color: var(--green);
    }
    select option { background: var(--surface); }

    /* Layout primitives */
    .cf-app { display: flex; height: 100vh; overflow: hidden; }
    .cf-sidebar {
      width: 230px; min-width: 230px;
      background: var(--bg2);
      border-right: 1px solid var(--border);
      display: flex; flex-direction: column;
      overflow-y: auto;
    }
    .cf-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
    .cf-topbar {
      height: 56px; min-height: 56px;
      background: var(--bg2);
      border-bottom: 1px solid var(--border);
      display: flex; align-items: center;
      padding: 0 24px;
      gap: 16px;
    }
    .cf-content { flex: 1; overflow-y: auto; padding: 24px; }
    
    /* Cards */
    .card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: 20px;
    }
    .card-sm { padding: 14px 16px; }
    .card:hover { border-color: var(--border2); }
    
    /* Buttons */
    .btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 10px 20px;
      border-radius: var(--radius);
      font-size: 14px; font-weight: 500;
      border: none;
      transition: all var(--transition);
    }
    .btn-primary {
      background: var(--green); color: #000;
      font-weight: 600;
    }
    .btn-primary:hover { background: #00ffb3; transform: translateY(-1px); box-shadow: 0 4px 20px var(--green-glow); }
    .btn-ghost {
      background: transparent; color: var(--text2);
      border: 1px solid var(--border);
    }
    .btn-ghost:hover { background: var(--surface2); color: var(--text); border-color: var(--border2); }
    .btn-danger { background: var(--red-dim); color: var(--red); border: 1px solid rgba(255,71,87,0.2); }
    .btn-danger:hover { background: rgba(255,71,87,0.2); }
    .btn-sm { padding: 6px 14px; font-size: 13px; }
    .btn-xs { padding: 4px 10px; font-size: 12px; }
    .btn-full { width: 100%; justify-content: center; }
    .btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none !important; }
    
    /* Status badges */
    .badge {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 3px 10px; border-radius: 100px;
      font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
    }
    .badge-green { background: var(--green-dim); color: var(--green); }
    .badge-red { background: var(--red-dim); color: var(--red); }
    .badge-amber { background: var(--amber-dim); color: var(--amber); }
    .badge-blue { background: var(--blue-dim); color: var(--blue); }
    .badge-gray { background: rgba(255,255,255,0.05); color: var(--text3); }
    
    /* Progress bars */
    .progress-track { height: 6px; background: var(--surface2); border-radius: 3px; overflow: hidden; }
    .progress-fill { height: 100%; border-radius: 3px; transition: width 0.6s ease; }
    
    /* Typography */
    .page-title { font-family: var(--font-display); font-size: 22px; font-weight: 700; }
    .section-title { font-family: var(--font-display); font-size: 15px; font-weight: 600; color: var(--text2); text-transform: uppercase; letter-spacing: 0.08em; }
    .mono { font-family: var(--font-mono); }
    
    /* Grid helpers */
    .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
    
    /* Tables */
    .cf-table { width: 100%; border-collapse: collapse; }
    .cf-table th {
      text-align: left; padding: 10px 14px;
      font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em;
      color: var(--text3); border-bottom: 1px solid var(--border);
    }
    .cf-table td { padding: 12px 14px; border-bottom: 1px solid var(--border); font-size: 13px; }
    .cf-table tr:last-child td { border-bottom: none; }
    .cf-table tr:hover td { background: rgba(255,255,255,0.02); }
    
    /* Modal */
    .modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000;
    }
    .modal {
      background: var(--bg3);
      border: 1px solid var(--border2);
      border-radius: var(--radius-lg);
      padding: 28px;
      width: 100%; max-width: 480px;
      animation: fadeIn 0.2s ease;
    }
    
    /* Trading UI */
    .candle-up { fill: var(--green); }
    .candle-down { fill: var(--red); }
    
    /* Landing page */
    .landing { min-height: 100vh; background: var(--bg); overflow-x: hidden; }
    .landing-nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      display: flex; align-items: center; justify-content: space-between;
      padding: 18px 60px;
      background: rgba(8,10,15,0.85); backdrop-filter: blur(16px);
      border-bottom: 1px solid var(--border);
    }
    .hero {
      min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 120px 60px 80px;
      position: relative; text-align: center;
    }
    .hero-bg {
      position: absolute; inset: 0;
      background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,229,160,0.06) 0%, transparent 70%),
                  radial-gradient(ellipse 60% 40% at 20% 80%, rgba(61,139,255,0.04) 0%, transparent 60%);
      pointer-events: none;
    }
    .hero-grid {
      position: absolute; inset: 0;
      background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
      background-size: 60px 60px;
      pointer-events: none;
    }
    
    /* Sidebar nav */
    .nav-item {
      display: flex; align-items: center; gap: 10px;
      padding: 9px 16px; margin: 1px 8px;
      border-radius: 7px;
      font-size: 13.5px; color: var(--text2);
      cursor: pointer; transition: all var(--transition);
      text-decoration: none;
    }
    .nav-item:hover { background: var(--surface); color: var(--text); }
    .nav-item.active { background: var(--green-dim); color: var(--green); }
    .nav-section {
      padding: 16px 16px 6px;
      font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em;
      color: var(--text3);
    }
    
    /* Metric cards */
    .metric-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: 18px 20px;
    }
    .metric-value { font-family: var(--font-display); font-size: 26px; font-weight: 700; }
    .metric-label { font-size: 12px; color: var(--text2); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
    .metric-change { font-size: 12px; font-family: var(--font-mono); }
    .metric-up { color: var(--green); }
    .metric-down { color: var(--red); }
    
    /* Rule meter */
    .rule-meter { padding: 14px 0; border-bottom: 1px solid var(--border); }
    .rule-meter:last-child { border-bottom: none; }
    
    /* Notifications */
    .toast {
      position: fixed; bottom: 24px; right: 24px; z-index: 9999;
      background: var(--surface2); border: 1px solid var(--border2);
      border-radius: var(--radius);
      padding: 12px 18px;
      display: flex; align-items: center; gap: 10px;
      font-size: 14px;
      animation: fadeIn 0.2s ease;
      box-shadow: 0 8px 32px rgba(0,0,0,0.4);
    }
    
    /* Chart container */
    .chart-container { position: relative; overflow: hidden; }
    
    /* Ticker tape */
    .ticker-tape {
      overflow: hidden; white-space: nowrap;
      background: var(--bg2); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
      padding: 8px 0;
    }
    .ticker-inner { display: inline-flex; gap: 60px; animation: ticker 30s linear infinite; }
    
    /* Responsive helpers */
    @media (max-width: 768px) {
      .cf-sidebar { display: none; }
      .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; }
      .landing-nav { padding: 16px 24px; }
      .hero { padding: 100px 24px 60px; }
    }
    
    /* Special effects */
    .glow-green { animation: glow 2s ease-in-out infinite; }
    .dot-pulse { width: 8px; height: 8px; border-radius: 50%; background: var(--green); animation: pulse 1.5s ease infinite; display: inline-block; }
    .dot-red { background: var(--red); }
    .dot-amber { background: var(--amber); }
    
    /* Form groups */
    .form-group { display: flex; flex-direction: column; gap: 6px; }
    .form-label { font-size: 13px; font-weight: 500; color: var(--text2); }
    
    /* Dividers */
    .divider { height: 1px; background: var(--border); margin: 16px 0; }
    
    /* Scrollable panel */
    .scroll-panel { overflow-y: auto; }
    
    /* Number displays */
    .num-positive { color: var(--green); font-family: var(--font-mono); }
    .num-negative { color: var(--red); font-family: var(--font-mono); }
    .num-neutral { font-family: var(--font-mono); }
    
    /* Plan cards */
    .plan-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: 28px;
      transition: all 0.2s;
      cursor: pointer;
    }
    .plan-card:hover { border-color: var(--border2); transform: translateY(-2px); }
    .plan-card.selected { border-color: var(--green); background: rgba(0,229,160,0.04); }
    .plan-card.featured { border-color: rgba(61,139,255,0.4); }
    
    /* Trading interface */
    .trading-layout {
      display: grid;
      grid-template-columns: 1fr 300px;
      grid-template-rows: 62% 38%;
      gap: 6px;
      height: 100%;
    }
    .chart-panel {
      background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius);
      grid-row: 1; grid-column: 1;
      overflow: hidden;
    }
    .order-panel {
      background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius);
      grid-row: 1 / 3; grid-column: 2;
      overflow-y: auto;
      display: flex; flex-direction: column;
    }
    .positions-panel {
      background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius);
      grid-row: 2; grid-column: 1;
      overflow: auto;
    }

    /* Order entry specific */
    .qty-btn {
      background: var(--surface2); border: 1px solid var(--border); color: var(--text2);
      border-radius: 5px; padding: 5px 10px; font-size: 12px; font-weight: 600;
      cursor: pointer; transition: all 0.15s; font-family: var(--font-mono);
    }
    .qty-btn:hover { border-color: var(--green); color: var(--green); background: var(--green-dim); }
    .qty-btn.active { border-color: var(--green); color: var(--green); background: var(--green-dim); }
    .buy-btn {
      background: var(--green); color: #000; border: none;
      border-radius: var(--radius); padding: 14px; font-size: 15px; font-weight: 800;
      cursor: pointer; transition: all 0.15s; font-family: var(--font-display);
      letter-spacing: 0.05em; flex: 1;
    }
    .buy-btn:hover { background: #00ffb3; box-shadow: 0 0 20px rgba(0,229,160,0.4); }
    .sell-btn {
      background: var(--red); color: #fff; border: none;
      border-radius: var(--radius); padding: 14px; font-size: 15px; font-weight: 800;
      cursor: pointer; transition: all 0.15s; font-family: var(--font-display);
      letter-spacing: 0.05em; flex: 1;
    }
    .sell-btn:hover { background: #ff2035; box-shadow: 0 0 20px rgba(255,71,87,0.4); }
    .flatten-btn {
      background: rgba(255,165,2,0.1); color: var(--amber); border: 1px solid rgba(255,165,2,0.3);
      border-radius: var(--radius); padding: 9px; font-size: 12px; font-weight: 700;
      cursor: pointer; transition: all 0.15s; width: 100%; font-family: var(--font-display);
      letter-spacing: 0.06em;
    }
    .flatten-btn:hover { background: rgba(255,165,2,0.2); border-color: var(--amber); }
    .bid-ask-box {
      display: flex; border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden;
    }
    .bid-box { flex: 1; padding: 8px 10px; background: rgba(255,71,87,0.06); border-right: 1px solid var(--border); }
    .ask-box { flex: 1; padding: 8px 10px; background: rgba(0,229,160,0.06); }
    .bid-label { font-size: 10px; color: var(--red); font-weight: 700; letter-spacing: 0.08em; margin-bottom: 2px; }
    .ask-label { font-size: 10px; color: var(--green); font-weight: 700; letter-spacing: 0.08em; margin-bottom: 2px; }
    .bid-price { font-family: var(--font-mono); font-size: 15px; color: var(--red); font-weight: 600; }
    .ask-price { font-family: var(--font-mono); font-size: 15px; color: var(--green); font-weight: 600; }

    /* Risk bar revamp */
    .risk-bar {
      display: flex; align-items: center; gap: 0;
      background: var(--bg2); border-bottom: 1px solid var(--border);
      padding: 0; height: 38px; overflow: hidden;
    }
    .risk-item {
      display: flex; flex-direction: column; justify-content: center;
      padding: 0 18px; border-right: 1px solid var(--border); height: 100%;
    }
    .risk-label { font-size: 9px; color: var(--text3); text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
    .risk-value { font-family: var(--font-mono); font-size: 13px; font-weight: 600; }

    /* History table export row */
    .export-bar { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-bottom: 1px solid var(--border); }
  
    
    /* Overrides */
    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button { opacity: 0.4; }
  `}</style>
);

// ─── ICONS (inline SVGs) ─────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const icons = {
    home: <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" stroke={color} strokeWidth="1.5" fill="none"/>,
    chart: <><polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke={color} strokeWidth="1.5" fill="none"/></>,
    dollar: <><line x1="12" y1="1" x2="12" y2="23" stroke={color} strokeWidth="1.5"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke={color} strokeWidth="1.5" fill="none"/></>,
    users: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke={color} strokeWidth="1.5" fill="none"/><circle cx="9" cy="7" r="4" stroke={color} strokeWidth="1.5" fill="none"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke={color} strokeWidth="1.5" fill="none"/></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke={color} strokeWidth="1.5" fill="none"/>,
    settings: <><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5" fill="none"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke={color} strokeWidth="1.5" fill="none"/></>,
    logout: <><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke={color} strokeWidth="1.5" fill="none"/></>,
    trending: <><polyline points="23,6 13.5,15.5 8.5,10.5 1,18" stroke={color} strokeWidth="1.5" fill="none"/><polyline points="17,6 23,6 23,12" stroke={color} strokeWidth="1.5" fill="none"/></>,
    alert: <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke={color} strokeWidth="1.5" fill="none"/><line x1="12" y1="9" x2="12" y2="13" stroke={color} strokeWidth="1.5"/><line x1="12" y1="17" x2="12.01" y2="17" stroke={color} strokeWidth="2"/></>,
    check: <polyline points="20,6 9,17 4,12" stroke={color} strokeWidth="2" fill="none"/>,
    x: <><line x1="18" y1="6" x2="6" y2="18" stroke={color} strokeWidth="1.5"/><line x1="6" y1="6" x2="18" y2="18" stroke={color} strokeWidth="1.5"/></>,
    eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke={color} strokeWidth="1.5" fill="none"/><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5" fill="none"/></>,
    file: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke={color} strokeWidth="1.5" fill="none"/><polyline points="14,2 14,8 20,8" stroke={color} strokeWidth="1.5" fill="none"/></>,
    inbox: <><polyline points="22,12 16,12 14,15 10,15 8,12 2,12" stroke={color} strokeWidth="1.5" fill="none"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" stroke={color} strokeWidth="1.5" fill="none"/></>,
    zap: <><polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" stroke={color} strokeWidth="1.5" fill="none"/></>,
    globe: <><circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" fill="none"/><line x1="2" y1="12" x2="22" y2="12" stroke={color} strokeWidth="1.5"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke={color} strokeWidth="1.5" fill="none"/></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke={color} strokeWidth="1.5" fill="none"/><path d="M7 11V7a5 5 0 0110 0v4" stroke={color} strokeWidth="1.5" fill="none"/></>,
    plus: <><line x1="12" y1="5" x2="12" y2="19" stroke={color} strokeWidth="1.5"/><line x1="5" y1="12" x2="19" y2="12" stroke={color} strokeWidth="1.5"/></>,
    refresh: <><polyline points="23,4 23,10 17,10" stroke={color} strokeWidth="1.5" fill="none"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" stroke={color} strokeWidth="1.5" fill="none"/></>,
    arrow_up: <><line x1="12" y1="19" x2="12" y2="5" stroke={color} strokeWidth="1.5"/><polyline points="5,12 12,5 19,12" stroke={color} strokeWidth="1.5" fill="none"/></>,
    arrow_down: <><line x1="12" y1="5" x2="12" y2="19" stroke={color} strokeWidth="1.5"/><polyline points="19,12 12,19 5,12" stroke={color} strokeWidth="1.5" fill="none"/></>,
    kyc: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke={color} strokeWidth="1.5" fill="none"/><circle cx="12" cy="7" r="4" stroke={color} strokeWidth="1.5" fill="none"/><polyline points="16,11 17.5,12.5 21,9" stroke={color} strokeWidth="2" fill="none"/></>,
    copy: <><rect x="9" y="9" width="13" height="13" rx="2" stroke={color} strokeWidth="1.5" fill="none"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke={color} strokeWidth="1.5" fill="none"/></>,
    tag: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke={color} strokeWidth="1.5" fill="none"/><line x1="7" y1="7" x2="7.01" y2="7" stroke={color} strokeWidth="2"/></>,
    bar_chart: <><line x1="18" y1="20" x2="18" y2="10" stroke={color} strokeWidth="1.5"/><line x1="12" y1="20" x2="12" y2="4" stroke={color} strokeWidth="1.5"/><line x1="6" y1="20" x2="6" y2="14" stroke={color} strokeWidth="1.5"/></>,
    activity: <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke={color} strokeWidth="1.5" fill="none"/>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "inline-block", flexShrink: 0 }}>
      {icons[name] || <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" fill="none"/>}
    </svg>
  );
};

// ─── CONSTANTS ───────────────────────────────────────────────
const PLANS = {
  core: {
    name: "Core",
    color: "#3D8BFF",
    prices: { "50K": 99, "100K": 139, "150K": 179 },
    payoutCap: (count) => 4000,
    payoutCapLabel: "$4,000 per payout (forever)",
    profitTarget: { "50K": 2500, "100K": 5000, "150K": 7500 },
    maxDrawdown: { "50K": 2500, "100K": 5000, "150K": 7500 },
    dailyLoss: { "50K": 1250, "100K": 2500, "150K": 3750 },
    minTradingDays: 5,
    description: "Consistent growth, capped payouts",
    features: ["5% profit target", "5% max drawdown", "2.5% daily loss limit", "$4K payout cap forever", "Min 5 trading days"],
  },
  velocity: {
    name: "Velocity",
    color: "#00E5A0",
    prices: { "50K": 199, "100K": 269, "150K": 319 },
    payoutCap: (count) => count <= 2 ? 3000 : count <= 5 ? 6000 : Infinity,
    payoutCapLabel: "1-2: $3K | 3-5: $6K | 6+: Unlimited",
    profitTarget: { "50K": 2500, "100K": 5000, "150K": 7500 },
    maxDrawdown: { "50K": 3000, "100K": 6000, "150K": 9000 },
    dailyLoss: { "50K": 1500, "100K": 3000, "150K": 4500 },
    minTradingDays: 3,
    description: "Fast track to unlimited payouts",
    features: ["5% profit target", "6% max drawdown", "3% daily loss limit", "Scales to unlimited", "Min 3 trading days", "Priority processing"],
  },
};

const ACCOUNT_SIZES = ["50K", "100K", "150K"];
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PRICE_IDS = {
  core: {
    "50K": "price_1T6aPj061uHESxWUg3yd3C8b",
    "100K": "price_1T6aPs061uHESxWUaEfCx9hA",
    "150K": "price_1T6aQ1061uHESxWUUzH670Dv",
  },
  velocity: {
    "50K": "price_1T6aQ9061uHESxWUWwOLO9CI",
    "100K": "price_1T6aQG061uHESxWU1bu7Us33",
    "150K": "price_1T6aQN061uHESxWUttKMWHyp",
  },
};

// ─── MOCK STATE / DB ─────────────────────────────────────────
const mockUser = {
  id: "usr_001", email: "alex@example.com", name: "Alex Rivera",
  role: "trader", kycStatus: "approved", created: "2025-11-01",
};

const mockAccount = {
  id: "acc_001", plan: "velocity", size: "100K",
  status: "active_funded", balance: 102_450,
  startBalance: 100_000, highWaterMark: 102_450,
  dailyPnl: 1_250, totalPnl: 2_450,
  profitTarget: 5000, maxDrawdown: 6000, dailyLossLimit: 3000,
  drawdownUsed: 1200, dailyLossUsed: 450,
  tradingDays: 8, minTradingDays: 3,
  payoutCount: 1, lastPayout: null,
  createdAt: "2025-11-15",
};

const mockTrades = [
  { id: "t1", symbol: "NQ", side: "buy", qty: 1, entry: 21_850.50, exit: 21_912.25, pnl: 1235, time: "09:32 AM", status: "closed" },
  { id: "t2", symbol: "ES", side: "sell", qty: 2, entry: 5_912.00, exit: 5_904.50, pnl: 750, time: "10:15 AM", status: "closed" },
  { id: "t3", symbol: "MNQ", side: "buy", qty: 5, entry: 21_880.00, exit: 21_859.00, pnl: -210, time: "11:03 AM", status: "closed" },
  { id: "t4", symbol: "NQ", side: "buy", qty: 1, entry: 21_920.00, exit: null, pnl: 525, time: "02:10 PM", status: "open" },
];

const mockPayouts = [
  { id: "py_001", amount: 2400, status: "paid", requested: "2025-12-01", paid: "2025-12-03", payoutNum: 1 },
];

const mockAdminUsers = [
  { id: "usr_001", name: "Alex Rivera", email: "alex@example.com", plan: "velocity", size: "100K", status: "active_funded", kyc: "approved", balance: 102450 },
  { id: "usr_002", name: "Sam Chen", email: "sam@example.com", plan: "core", size: "50K", status: "active_evaluation", kyc: "approved", balance: 51200 },
  { id: "usr_003", name: "Jordan Lee", email: "jordan@example.com", plan: "velocity", size: "150K", status: "breached", kyc: "submitted", balance: 143000 },
  { id: "usr_004", name: "Morgan Wells", email: "morgan@example.com", plan: "core", size: "100K", status: "payout_requested", kyc: "approved", balance: 104500 },
  { id: "usr_005", name: "Taylor Kim", email: "taylor@example.com", plan: "velocity", size: "50K", status: "active_evaluation", kyc: "not_started", balance: 50000 },
];

const mockPayoutQueue = [
  { id: "py_002", trader: "Morgan Wells", email: "morgan@example.com", plan: "core", amount: 3800, payoutNum: 2, cap: 4000, kyc: "approved", risk: "low", requested: "2025-12-08" },
  { id: "py_003", trader: "Dana Scott", email: "dana@example.com", plan: "velocity", amount: 2800, payoutNum: 1, cap: 3000, kyc: "approved", risk: "low", requested: "2025-12-09" },
];

// ─── UTILITIES ───────────────────────────────────────────────
const fmt = {
  usd: (n) => {
    if (n === Infinity) return "Unlimited";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n);
  },
  pct: (n, d = 1) => `${n.toFixed(d)}%`,
  date: (s) => new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
};

const statusMeta = {
  active_funded: { label: "Funded Active", badge: "badge-green" },
  active_evaluation: { label: "Evaluation", badge: "badge-blue" },
  passed_evaluation: { label: "Passed Eval", badge: "badge-green" },
  payout_requested: { label: "Payout Pending", badge: "badge-amber" },
  breached: { label: "Breached", badge: "badge-red" },
  suspended: { label: "Suspended", badge: "badge-red" },
  closed: { label: "Closed", badge: "badge-gray" },
};

const kycMeta = {
  approved: { label: "Approved", badge: "badge-green" },
  submitted: { label: "Under Review", badge: "badge-amber" },
  not_started: { label: "Not Started", badge: "badge-gray" },
  rejected: { label: "Rejected", badge: "badge-red" },
};

// ─── TOAST SYSTEM ────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const iconName = toast.type === "success" ? "check" : toast.type === "error" ? "x" : "alert";
  const color = toast.type === "success" ? "var(--green)" : toast.type === "error" ? "var(--red)" : "var(--amber)";
  return (
    <div className="toast">
      <Icon name={iconName} color={color} size={16} />
      <span>{toast.msg}</span>
    </div>
  );
};

// ─── CANDLESTICK CHART (SIM) ─────────────────────────────────
const CandleChart = ({ symbol = "NQ", priceRef, startPrice, livePrice }) => {
  const candles = useRef([]);
  const [, setTick] = useState(0);
  const [timeframe, setTimeframe] = useState("1m");
  const [offset, setOffset] = useState(0);
  const [zoom, setZoom] = useState(60);
  const isDragging = useRef(false);
  const dragStart = useRef(null);
  const svgRef = useRef(null);

  const timeframes = ["1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","6h","8h","12h","1D","3D","1W","1M"];
  const intervals  = { "1m":800,"2m":1200,"3m":1600,"5m":2000,"10m":2500,"15m":3000,"30m":4000,"1h":5000,"2h":6000,"4h":7000,"6h":8000,"8h":9000,"12h":10000,"1D":12000,"3D":14000,"1W":16000,"1M":20000 };
  const candleSizes= { "1m":6,"2m":8,"3m":10,"5m":15,"10m":20,"15m":30,"30m":45,"1h":60,"2h":80,"4h":110,"6h":130,"8h":150,"12h":180,"1D":220,"3D":300,"1W":400,"1M":600 };

  // ── 1) Candle generation — reruns when timeframe or symbol changes ──────
  useEffect(() => {
    const volatility = candleSizes[timeframe];
    candles.current = [];
    const basePrices = { NQ: 21850, ES: 5900, MNQ: 21850, MES: 5900 };
    let price = basePrices[symbol] || startPrice || 21850;
    for (let i = 200; i >= 0; i--) {
      const o = price + (Math.random() - 0.5) * volatility;
      const c = o + (Math.random() - 0.5) * volatility * 1.5;
      const h = Math.max(o, c) + Math.random() * volatility * 0.5;
      const l = Math.min(o, c) - Math.random() * volatility * 0.5;
      candles.current.push({ o, h, l, c });
      price = c;
    }
    setOffset(0);
  }, [timeframe, symbol]);

  // ── 2) Mock tick — only runs when Tradovate is NOT connected ─────────────
  useEffect(() => {
    if (livePrice != null) return;
    const volatility = candleSizes[timeframe];
    const speed = intervals[timeframe];
    const iv = setInterval(() => {
      const last = candles.current[candles.current.length - 1];
      if (!last) return;
      const newC = last.c + (Math.random() - 0.49) * (volatility * 0.3);
      candles.current[candles.current.length - 1] = {
        ...last,
        c: newC,
        h: Math.max(last.h, newC),
        l: Math.min(last.l, newC),
      };
      if (priceRef) priceRef.current = newC;
      setTick(t => t + 1);
    }, speed);
    return () => clearInterval(iv);
  }, [timeframe, symbol, livePrice]);

  // ── 3) Live price injection — updates last candle with real Tradovate price
  useEffect(() => {
    if (livePrice == null || candles.current.length === 0) return;
    const last = candles.current[candles.current.length - 1];
    candles.current[candles.current.length - 1] = {
      ...last,
      c: livePrice,
      h: Math.max(last.h, livePrice),
      l: Math.min(last.l, livePrice),
    };
    if (priceRef) priceRef.current = livePrice;
    setTick(t => t + 1);
  }, [livePrice]);

  // ── Pan handlers ──────────────────────────────────────────────────────────
  const onMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX, offset };
  };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const candleWidth = 800 / zoom;
    const delta = Math.round(dx / candleWidth);
    const newOffset = Math.max(0, Math.min(candles.current.length - zoom, dragStart.current.offset - delta));
    setOffset(newOffset);
  };
  const onMouseUp = () => { isDragging.current = false; };

  // ── Zoom handler ──────────────────────────────────────────────────────────
  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 5 : -5;
    setZoom(z => Math.max(10, Math.min(150, z + delta)));
  };

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const W = 700, H = 260;
  const totalCandles = candles.current.length;
  const startIdx = Math.max(0, totalCandles - zoom - offset);
  const endIdx   = Math.max(zoom, totalCandles - offset);
  const visible  = candles.current.slice(startIdx, endIdx).slice(-zoom);

  const prices = visible.flatMap(c => [c.h, c.l]);
  const minP  = Math.min(...prices) || 0;
  const maxP  = Math.max(...prices) || 1;
  const range = maxP - minP || 1;
  const pad   = { top: 20, bottom: 30, left: 10, right: 60 };
  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top  - pad.bottom;
  const cw = chartW / visible.length;
  const bw = Math.max(1, cw * 0.6);

  const py = (p) => pad.top + chartH - ((p - minP) / range) * chartH;
  const px = (i) => pad.left + i * cw + cw / 2;

  const last = visible[visible.length - 1] || { c: 0, o: 0, h: 0, l: 0 };
  const isUp = last.c >= last.o;

  return (
    <div style={{ position: "relative", background: "var(--surface)", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>{symbol}</span>
        <span className="badge badge-gray" style={{ fontSize: 10 }}>SIM</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 18, color: isUp ? "var(--green)" : "var(--red)" }}>
          {last.c.toFixed(2)}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: isUp ? "var(--green)" : "var(--red)" }}>
          {isUp ? "▲" : "▼"} {Math.abs(last.c - last.o).toFixed(2)}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4, flexWrap: "wrap" }}>
          {timeframes.map(tf => (
            <button key={tf} className={`btn btn-xs ${timeframe === tf ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setTimeframe(tf)}
              style={{ padding: "2px 6px", fontSize: 10 }}
            >{tf}</button>
          ))}
        </div>
      </div>

      {/* Zoom indicator */}
      <div style={{ display: "flex", gap: 16, padding: "4px 16px", fontSize: 10, color: "var(--text3)", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <span>Scroll to zoom · Drag to pan</span>
        <span>{zoom} candles visible</span>
        {offset > 0 && <span style={{ color: "var(--amber)" }}>← {offset} candles back</span>}
      </div>

      {/* SVG Chart */}
      <svg
        ref={svgRef}
        width="100%" viewBox={`0 0 ${W} ${H}`}
        style={{ display: "block", flex: 1, cursor: isDragging.current ? "grabbing" : "grab" }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(r => {
          const y = pad.top + r * chartH;
          const p = maxP - r * range;
          return (
            <g key={r}>
              <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
              <text x={W - pad.right + 4} y={y + 4} fill="var(--text3)" fontSize="9" fontFamily="monospace">{p.toFixed(0)}</text>
            </g>
          );
        })}

        {/* Candles */}
        {visible.map((c, i) => {
          const up  = c.c >= c.o;
          const col = up ? "var(--green)" : "var(--red)";
          const cx  = px(i);
          const bodyTop = py(Math.max(c.o, c.c));
          const bodyH   = Math.max(1, Math.abs(py(c.o) - py(c.c)));
          const isLast  = i === visible.length - 1 && offset === 0;
          return (
            <g key={i} style={isLast ? { animation: "candleFlash 1.6s ease infinite" } : {}}>
              <line x1={cx} y1={py(c.h)} x2={cx} y2={py(c.l)} stroke={col} strokeWidth="1"/>
              <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={col} rx="0.5"/>
            </g>
          );
        })}

        {/* Current price line */}
        <line x1={pad.left} y1={py(last.c)} x2={W - pad.right} y2={py(last.c)} stroke="rgba(255,255,255,0.2)" strokeDasharray="4,4" strokeWidth="1"/>
        <rect x={W - pad.right} y={py(last.c) - 8} width={pad.right} height={16} fill={isUp ? "var(--green)" : "var(--red)"} rx="2"/>
        <text x={W - pad.right + 4} y={py(last.c) + 4} fill="#000" fontSize="9" fontFamily="monospace" fontWeight="bold">{last.c.toFixed(0)}</text>

        {/* Volume bars */}
        {visible.map((c, i) => {
          const vol = 0.2 + Math.random() * 0.8;
          const bh  = vol * 20;
          return (
            <rect key={i} x={px(i) - bw/2} y={H - pad.bottom - bh} width={bw} height={bh}
              fill={c.c >= c.o ? "rgba(0,229,160,0.25)" : "rgba(255,71,87,0.25)"}/>
          );
        })}
      </svg>
    </div>
  );
};

// ─── ORDER ENTRY ─────────────────────────────────────────────
const OrderEntry = ({ onFill, onSymbolChange, onFlatten, positions = [], liveBid, liveAsk, livePrice }) => {
  const [qty, setQty] = useState(1);
  const [type, setType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [symbol, setSymbol] = useState("NQ");
  const [submitting, setSubmitting] = useState(false);

  const instruments = {
    NQ:  { tick: 0.25, tickVal: 5,    name: "E-mini Nasdaq 100",  maxRisk: 5 },
    ES:  { tick: 0.25, tickVal: 12.5, name: "E-mini S&P 500",     maxRisk: 5 },
    MNQ: { tick: 0.25, tickVal: 0.5,  name: "Micro Nasdaq 100",   maxRisk: 5 },
    MES: { tick: 0.25, tickVal: 1.25, name: "Micro S&P 500",      maxRisk: 5 },
  };

  const inst = instruments[symbol];
  const maxRiskDollars = inst.tickVal * inst.maxRisk * qty;
  const qtyPresets = [1, 2, 3, 5, 10];

  const handleSubmit = (side) => {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onFill && onFill({ symbol, side, qty: parseInt(qty), type });
    }, 400);
  };

  const hasPosition = positions.some(p => p.symbol === symbol);
  const bid = liveBid ?? (livePrice ? livePrice - 0.25 : null);
  const ask = liveAsk ?? (livePrice ? livePrice + 0.25 : null);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", background: "rgba(0,0,0,0.2)" }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Order Entry</div>
      </div>

      <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>

        {/* Instrument selector */}
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Instrument</div>
          <select value={symbol} onChange={e => { setSymbol(e.target.value); onSymbolChange && onSymbolChange(e.target.value); }}
            style={{ background: "var(--surface)", border: "1px solid var(--border2)", color: "var(--text)", borderRadius: "var(--radius)", padding: "9px 12px", fontSize: 13, width: "100%", fontWeight: 600 }}>
            {Object.entries(instruments).map(([sym, info]) => (
              <option key={sym} value={sym}>{sym} — {info.name}</option>
            ))}
          </select>
        </div>

        {/* Bid / Ask */}
        <div className="bid-ask-box">
          <div className="bid-box">
            <div className="bid-label">BID</div>
            <div className="bid-price">{bid ? bid.toFixed(2) : "—"}</div>
          </div>
          <div className="ask-box">
            <div className="ask-label">ASK</div>
            <div className="ask-price">{ask ? ask.toFixed(2) : "—"}</div>
          </div>
        </div>

        {/* Order Type */}
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Order Type</div>
          <div style={{ display: "flex", gap: 4 }}>
            {["market", "limit"].map(t => (
              <button key={t} onClick={() => setType(t)}
                style={{ flex: 1, padding: "7px", borderRadius: "var(--radius)", border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em",
                  background: type === t ? "var(--surface2)" : "transparent",
                  borderColor: type === t ? "var(--border2)" : "var(--border)",
                  color: type === t ? "var(--text)" : "var(--text3)" }}>
                {t}
              </button>
            ))}
          </div>
          {type === "limit" && (
            <input type="number" value={limitPrice} onChange={e => setLimitPrice(e.target.value)}
              placeholder="Limit price" style={{ marginTop: 8, background: "var(--surface)", border: "1px solid var(--border2)", color: "var(--text)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13, width: "100%" }}/>
          )}
        </div>

        {/* Quantity */}
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Contracts</div>
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {qtyPresets.map(q => (
              <button key={q} className={`qty-btn ${qty === q ? "active" : ""}`} onClick={() => setQty(q)}>{q}</button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => setQty(q => Math.max(1, q - 1))}
              style={{ width: 30, height: 30, borderRadius: 6, background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 300 }}>−</button>
            <div style={{ flex: 1, textAlign: "center", fontFamily: "var(--font-mono)", fontSize: 20, fontWeight: 700, color: "var(--text)" }}>{qty}</div>
            <button onClick={() => setQty(q => q + 1)}
              style={{ width: 30, height: 30, borderRadius: 6, background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 300 }}>+</button>
          </div>
        </div>

        {/* Tick info */}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "var(--surface)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11 }}>
            <div style={{ color: "var(--text3)", fontSize: 10, marginBottom: 2 }}>Tick Value</div>
            <div className="mono" style={{ color: "var(--text)", fontWeight: 600 }}>${inst.tickVal}/tick</div>
          </div>
          <div style={{ fontSize: 11, textAlign: "right" }}>
            <div style={{ color: "var(--text3)", fontSize: 10, marginBottom: 2 }}>Max Risk (5 ticks)</div>
            <div className="mono" style={{ color: "var(--amber)", fontWeight: 600 }}>${maxRiskDollars}</div>
          </div>
        </div>

        {/* BUY / SELL buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="buy-btn" onClick={() => handleSubmit("buy")} disabled={submitting}>
            ▲ BUY {qty} {symbol}
          </button>
          <button className="sell-btn" onClick={() => handleSubmit("sell")} disabled={submitting}>
            ▼ SELL {qty} {symbol}
          </button>
        </div>

        {/* Flatten / Close All */}
        {hasPosition && (
          <button className="flatten-btn" onClick={() => onFlatten && onFlatten()}>
            ⚡ FLATTEN ALL POSITIONS
          </button>
        )}

        {/* SIM mode notice */}
        <div style={{ textAlign: "center", fontSize: 11, color: "var(--text3)", marginTop: "auto", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
          ⚠ SIM MODE — No real money at risk
        </div>
      </div>
    </div>
  );
};

// Legacy compatibility shim — unused but kept
const _OrderEntryLegacy = ({ onFill, onSymbolChange }) => {
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState(1);
  const [type, setType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [symbol, setSymbol] = useState("NQ");
  const [submitting, setSubmitting] = useState(false);
  const instruments = {
    NQ:  { tick: 0.25, tickVal: 5,    name: "E-mini Nasdaq 100" },
    ES:  { tick: 0.25, tickVal: 12.5, name: "E-mini S&P 500" },
    MNQ: { tick: 0.25, tickVal: 0.5,  name: "Micro Nasdaq 100" },
    MES: { tick: 0.25, tickVal: 1.25, name: "Micro S&P 500" },
  };
  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); onFill && onFill({ symbol, side, qty: parseInt(qty), type }); }, 400);
  };
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Order Entry</div>
      <div className="form-group">
        <label className="form-label">Instrument</label>
        <select value={symbol} onChange={e => { setSymbol(e.target.value); onSymbolChange && onSymbolChange(e.target.value); }}>
          {Object.entries(instruments).map(([sym, info]) => (
            <option key={sym} value={sym}>{sym} — {info.name}</option>
          ))}
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <button className="btn" onClick={() => setSide("buy")}
          style={{ background: side === "buy" ? "var(--green)" : "var(--surface2)", color: side === "buy" ? "#000" : "var(--text2)", fontWeight: 700 }}
        >▲ BUY</button>
        <button className="btn" onClick={() => setSide("sell")}
          style={{ background: side === "sell" ? "var(--red)" : "var(--surface2)", color: side === "sell" ? "#fff" : "var(--text2)", fontWeight: 700 }}
        >▼ SELL</button>
      </div>

      <div className="form-group">
        <label className="form-label">Order Type</label>
        <select value={type} onChange={e => setType(e.target.value)}>
          <option value="market">Market</option>
          <option value="limit">Limit</option>
          <option value="stop">Stop</option>
        </select>
      </div>

      {type !== "market" && (
        <div className="form-group">
          <label className="form-label">Price</label>
          <input type="number" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} placeholder="0.00" step="0.25"/>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Contracts</label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" style={{ minWidth: 36 }} onClick={() => setQty(q => Math.max(1, q-1))}>−</button>
          <input type="number" value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))} style={{ textAlign: "center" }} min="1" max="10"/>
          <button className="btn btn-ghost btn-sm" style={{ minWidth: 36 }} onClick={() => setQty(q => Math.min(10, q+1))}>+</button>
        </div>
      </div>

      <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: "10px 12px", fontSize: 12, color: "var(--text2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span>Tick value</span>
          <span className="mono">{fmt.usd(instruments[symbol]?.tickVal * qty)}/tick</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Max risk (5 ticks)</span>
          <span className="mono" style={{ color: "var(--red)" }}>{fmt.usd(instruments[symbol]?.tickVal * qty * 5)}</span>
        </div>
      </div>

      <button
        className={`btn btn-full ${side === "buy" ? "btn-primary" : "btn-danger"}`}
        style={side === "sell" ? { background: "var(--red)", color: "#fff" } : {}}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? "Placing..." : `${side.toUpperCase()} ${qty} ${symbol}`}
      </button>
    </div>
  );
};

// ─── RULE METERS ─────────────────────────────────────────────
const RuleMeters = ({ account }) => {
  const plan = PLANS[account.plan?.toLowerCase()] || PLANS.velocity;
  const size = (account.size || 'SIZE_100K').replace('SIZE_', '');
  const isFunded = account.status === 'FUNDED' || account.status === 'active_funded';

  const rules = [
    { label: "Profit Target",    current: Number(account.totalPnl || 0),      limit: plan.profitTarget[size], type: "progress", color: "var(--green)" },
    { label: "Daily Loss Limit", current: Math.abs(Math.min(0, Number(account.dailyPnl || account.dailyLossUsed || 0))), limit: plan.dailyLoss[size], type: "risk", color: account.dailyLossUsed / plan.dailyLoss[size] > 0.7 ? "var(--red)" : "var(--amber)" },
    { label: "Max Drawdown",     current: Number(account.drawdownUsed || 0),  limit: plan.maxDrawdown[size],  type: "risk", color: account.drawdownUsed / plan.maxDrawdown[size] > 0.7 ? "var(--red)" : "var(--amber)" },
    { label: "Trading Days",     current: Number(account.tradingDays || 0),   limit: plan.minTradingDays,     type: "days", color: account.tradingDays >= plan.minTradingDays ? "var(--green)" : "var(--blue)" },
  ].filter(rule => isFunded ? rule.label !== "Profit Target" : true);

  return (
    <div>
      {rules.map((rule) => {
        const pct = Math.min(100, (rule.current / rule.limit) * 100);
        const remaining = rule.limit - rule.current;
        return (
          <div key={rule.label} className="rule-meter">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{rule.label}</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {rule.type === "days" ? (
                  <>
                    <span className="mono" style={{ fontSize: 12, color: rule.color }}>{rule.current}/{rule.limit} days</span>
                    {rule.current >= rule.limit && <span className="badge badge-green" style={{ fontSize: 9 }}>✓</span>}
                  </>
                ) : rule.type === "progress" ? (
                  <span className="mono" style={{ fontSize: 12, color: rule.color }}>{fmt.usd(rule.current)} / {fmt.usd(rule.limit)}</span>
                ) : (
                  <span className="mono" style={{ fontSize: 12, color: rule.color }}>{fmt.usd(remaining)} remaining</span>
                )}
              </div>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${pct}%`, background: rule.color }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── DASHBOARD PAGE ──────────────────────────────────────────
const DashboardPage = ({ account, trades }) => {
  const plan = PLANS[account.plan?.toLowerCase()] || PLANS.velocity;
  const pnlPct = ((Number(account.balance) - Number(account.startingBalance || account.startBalance)) / Number(account.startingBalance || account.startBalance || 1)) * 100;
  const statusMap = {
    'EVALUATION':  'active_evaluation',
    'PASSED':      'passed_evaluation',
    'FUNDED':      'active_funded',
    'BREACHED':    'breached',
    'PENDING_KYC': 'passed_evaluation',
    'INACTIVE':    'suspended',
  };
  const statusInfo = statusMeta[statusMap[account.status] || account.status] || { label: account.status || 'Active', badge: 'badge-gray' };
  const eqData = [0, 120, -80, 450, 380, 820, 650, 1100, 1400, 1800, 2200, 1900, 2450];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 4 }}>Account Overview</h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span className={`badge ${statusInfo.badge}`}><span className="dot-pulse" style={{ width: 6, height: 6, background: "currentColor" }}></span> {statusInfo.label}</span>
            <span className="badge badge-blue">{plan.name} Plan</span>
            <span className="badge badge-gray">{account.size} Account</span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 2 }}>Account ID</div>
          <div className="mono" style={{ fontSize: 12, color: "var(--text2)" }}>{account.id}</div>
        </div>
      </div>

      <div className="grid-4">
        {[
          { label: "Balance",     value: fmt.usd(account.balance),  change: `${pnlPct > 0 ? "+" : ""}${pnlPct.toFixed(2)}%`, up: pnlPct > 0 },
          { label: "Total P&L",  value: fmt.usd(account.totalPnl), change: `${pnlPct > 0 ? "+" : ""}${pnlPct.toFixed(2)}%`, up: account.totalPnl > 0 },
          { label: "Today's P&L",value: fmt.usd(account.dailyPnl), change: "Today", up: account.dailyPnl > 0 },
          { label: "Payout #",   value: account.payoutCount === 0 ? "None yet" : `#${account.payoutCount}`, change: `Next cap: ${fmt.usd(plan.payoutCap(account.payoutCount + 1))}`, up: true },
        ].map(m => (
          <div key={m.label} className="metric-card">
            <div className="metric-label" style={{ marginBottom: 6 }}>{m.label}</div>
            <div className="metric-value" style={{ color: m.up ? "var(--green)" : "var(--red)", marginBottom: 4 }}>{m.value}</div>
            <div className={`metric-change ${m.up ? "metric-up" : "metric-down"}`}>{m.change}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20 }}>
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>Equity Curve</div>
            <div style={{ display: "flex", gap: 6 }}>
              {["1D","1W","1M","All"].map(t => (
                <button key={t} className={`btn btn-ghost btn-xs ${t === "All" ? "active" : ""}`}
                  style={t === "All" ? { background: "var(--green-dim)", color: "var(--green)", borderColor: "transparent" } : {}}
                >{t}</button>
              ))}
            </div>
          </div>
          <svg width="100%" viewBox="0 0 400 120" style={{ overflow: "visible" }}>
            {[0, 0.5, 1].map(r => (
              <line key={r} x1="0" y1={100 * r + 10} x2="400" y2={100 * r + 10} stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            ))}
            <defs>
              <linearGradient id="eq_grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--green)" stopOpacity="0.2"/>
                <stop offset="100%" stopColor="var(--green)" stopOpacity="0"/>
              </linearGradient>
            </defs>
            {(() => {
              const pts = eqData.map((v, i) => {
                const x = (i / (eqData.length - 1)) * 380 + 10;
                const min = Math.min(...eqData), max = Math.max(...eqData);
                const y = 110 - ((v - min) / (max - min)) * 90;
                return `${x},${y}`;
              }).join(" ");
              const firstX = 10, lastX = 390;
              const lastY = (() => {
                const min = Math.min(...eqData), max = Math.max(...eqData);
                return 110 - ((eqData[eqData.length-1] - min) / (max - min)) * 90;
              })();
              return (
                <>
                  <polygon points={`${firstX},110 ${pts} ${lastX},110`} fill="url(#eq_grad)"/>
                  <polyline points={pts} fill="none" stroke="var(--green)" strokeWidth="2" strokeLinejoin="round"/>
                  <circle cx={lastX} cy={lastY} r="4" fill="var(--green)"/>
                  <circle cx={lastX} cy={lastY} r="8" fill="var(--green)" opacity="0.2"/>
                </>
              );
            })()}
          </svg>
        </div>

        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Rule Tracker</div>
          <RuleMeters account={account} />
        </div>
      </div>

      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Recent Trades</div>
        <table className="cf-table">
          <thead>
            <tr><th>Symbol</th><th>Side</th><th>Qty</th><th>Entry</th><th>Exit</th><th>P&L</th><th>Time</th><th>Status</th></tr>
          </thead>
          <tbody>
            {trades.map(t => (
              <tr key={t.id}>
                <td><span style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>{t.symbol}</span></td>
                <td><span className={`badge ${t.side === "buy" ? "badge-green" : "badge-red"}`} style={{ fontSize: 10 }}>{t.side.toUpperCase()}</span></td>
                <td className="mono">{t.qty || t.quantity}</td>
                <td className="mono">{(t.entry || t.entryPrice || 0).toLocaleString()}</td>
                <td className="mono">{(t.exit || t.exitPrice) ? (t.exit || t.exitPrice).toLocaleString() : <span style={{ color: "var(--text3)" }}>—</span>}</td>
                <td className={t.pnl >= 0 ? "num-positive" : "num-negative"}>{t.pnl >= 0 ? "+" : ""}{fmt.usd(t.pnl)}</td>
                <td style={{ color: "var(--text2)", fontSize: 12 }}>{t.time}</td>
                <td><span className={`badge ${t.status === "open" ? "badge-amber" : "badge-gray"}`} style={{ fontSize: 10 }}>{t.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── TRADING PAGE ─────────────────────────────────────────────
const TradingPage = ({ account, onAccountRefresh }) => {
  const [positions, setPositions] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [tab, setTab] = useState("positions");
  const [toast, setToast] = useState(null);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [closingId, setClosingId] = useState(null);
  const chartPriceRef = useRef(null);
  const [chartSymbol, setChartSymbol] = useState("NQ");

  const tradovateSymbol =
    chartSymbol === 'NQ'  ? 'NQM5'  :
    chartSymbol === 'ES'  ? 'ESM5'  :
    chartSymbol === 'MNQ' ? 'MNQM5' :
    chartSymbol === 'MES' ? 'MESM5' : chartSymbol;

  const { lastPrice: livePrice, bid: liveBid, ask: liveAsk, connected: mdConnected } = useMarketData(tradovateSymbol);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const loadData = async () => {
    if (!account?.id) { setLoadingPositions(false); return; }
    try {
      const [pos, hist] = await Promise.all([
        tradesApi.getPositions(account.id).catch(() => []),
        tradesApi.list({ accountId: account.id, status: 'CLOSED', limit: 50 }).catch(() => []),
      ]);
      setPositions(Array.isArray(pos) ? pos : []);
      const histList = hist?.trades ?? hist;
      setTradeHistory(Array.isArray(histList) ? histList : []);
    } catch (e) {
      // fail silently
    } finally {
      setLoadingPositions(false);
      if (onAccountRefresh) onAccountRefresh();
    }
  };

  useEffect(() => { loadData(); }, [account?.id]);

  const handleFill = async ({ symbol, side, qty }) => {
    if (!account?.id) { showToast('No active account found', 'error'); return; }
    const entryPrice = parseFloat((chartPriceRef.current || 21850 + Math.random() * 200).toFixed(2));
    const newSide      = side === 'buy' ? 'LONG' : 'SHORT';
    const oppositeSide = side === 'buy' ? 'SHORT' : 'LONG';
    const existingPosition = positions.find(p => p.symbol === symbol && p.side === oppositeSide);
    if (existingPosition) {
      await closePosition(existingPosition);
      return;
    }
    try {
      await tradesApi.open({ accountId: account.id, symbol, side: newSide, quantity: qty, entryPrice });
      await loadData();
      showToast(`${side.toUpperCase()} ${qty} ${symbol} filled @ ${entryPrice}`, 'success');
    } catch (err) {
      showToast(err.message || 'Order failed', 'error');
    }
  };

  const closePosition = async (position) => {
    setClosingId(position.id);
    const exitPrice = parseFloat((chartPriceRef.current || parseFloat(position.entryPrice) + (Math.random() - 0.5) * 40).toFixed(2));
    try {
      const openTrades = await tradesApi.list({ accountId: account.id, status: 'OPEN', limit: 100 }).catch(() => ({ trades: [] }));
      const tradeList = openTrades?.trades ?? [];
      const matchingTrades = tradeList.filter(t => t.symbol === position.symbol);
      if (matchingTrades.length === 0) {
        showToast('Could not find open trades to close', 'error');
        setClosingId(null);
        return;
      }
      let totalPnl = 0;
      let lastResult = null;
      for (const trade of matchingTrades) {
        lastResult = await tradesApi.close(trade.id, exitPrice);
        totalPnl += Number(lastResult.pnl ?? 0);
      }
      await loadData();
      const pnlStr = `${totalPnl >= 0 ? '+' : ''}${fmt.usd(totalPnl)}`;
      if (lastResult?.ruleResult?.breached) {
        showToast(`⚠ ACCOUNT BREACHED — Position auto-closed. Check your dashboard.`, 'error');
      } else if (lastResult?.ruleResult?.passed) {
        showToast(`🎉 EVALUATION PASSED! Congratulations — check your dashboard.`, 'success');
      } else {
        showToast(`Position closed @ ${exitPrice} — P&L: ${pnlStr}`, totalPnl >= 0 ? 'success' : 'error');
      }
    } catch (err) {
      showToast(err.message || 'Close failed', 'error');
    } finally {
      setClosingId(null);
    }
  };

  const flattenAll = async () => {
    for (const pos of positions) {
      await closePosition(pos);
    }
  };

  const planKey  = account?.plan?.toLowerCase() || 'core';
  const sizeKey  = (account?.size || 'SIZE_100K').replace('SIZE_', '');
  const plan     = PLANS[planKey] || PLANS.core;
  const dailyPnl = Number(account?.dailyPnl || 0);
  const dailyLossLimit = plan.dailyLoss[sizeKey] || 2000;
  const drawdownUsed   = Number(account?.drawdownUsed || 0);
  const maxDrawdown    = plan.maxDrawdown[sizeKey] || 5000;
  const dailyLossUsed  = Math.abs(Math.min(0, dailyPnl));
  const balance        = Number(account?.balance || 0);

  // Compute live unrealized P&L across all positions
  const totalUnrealPnl = positions.reduce((sum, p) => {
    const cur   = chartPriceRef.current || parseFloat(p.entryPrice);
    const entry = parseFloat(p.entryPrice);
    const qty   = Number(p.quantity);
    return sum + (p.side === 'LONG' ? (cur - entry) * qty : (entry - cur) * qty);
  }, 0);

  const riskItems = [
    { label: "Daily Loss Remaining", val: dailyLossLimit - dailyLossUsed, alert: dailyLossUsed / dailyLossLimit > 0.7, positive: undefined },
    { label: "Drawdown Remaining",   val: maxDrawdown - drawdownUsed,     alert: drawdownUsed / maxDrawdown > 0.7,     positive: undefined },
    { label: "Today's P&L",          val: dailyPnl,                       alert: false,                                positive: dailyPnl >= 0 },
    { label: "Balance",              val: balance,                         alert: false,                                positive: undefined },
  ];

  return (
    <div style={{ height: "calc(100vh - 56px)", display: "flex", flexDirection: "column", overflow: "hidden" }} className="fade-in">

      {/* ── Revamped Risk Bar ─────────────────────────────────────── */}
      <div className="risk-bar">
        <div className="risk-item" style={{ paddingLeft: 14 }}>
          <div className="risk-label">Risk Monitor</div>
          <div style={{ fontSize: 10, color: "var(--text3)" }}>
            {account?.plan?.toUpperCase() || 'VELOCITY'} · {(account?.size || '100K').replace('SIZE_', '')}
          </div>
        </div>
        {riskItems.map(r => (
          <div key={r.label} className="risk-item">
            <div className="risk-label">{r.label}</div>
            <div className="risk-value" style={{
              color: r.alert ? "var(--red)" : r.positive !== undefined ? (r.positive ? "var(--green)" : "var(--red)") : "var(--text)"
            }}>{fmt.usd(r.val)}</div>
          </div>
        ))}
        {positions.length > 0 && (
          <div className="risk-item">
            <div className="risk-label">Open P&L</div>
            <div className="risk-value" style={{ color: totalUnrealPnl >= 0 ? "var(--green)" : "var(--red)" }}>
              {totalUnrealPnl >= 0 ? "+" : ""}{fmt.usd(totalUnrealPnl)}
            </div>
          </div>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", paddingRight: 14 }}>
          <span className="dot-pulse" style={{ background: mdConnected ? "var(--green)" : "var(--amber)" }}/>
          <span style={{ color: mdConnected ? "var(--green)" : "var(--amber)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>
            {mdConnected ? "SIM LIVE" : "SIM MODE"}
          </span>
        </div>
      </div>

      {/* ── Main trading layout ───────────────────────────────────── */}
      <div className="trading-layout" style={{ flex: 1, padding: 6 }}>

        {/* Chart */}
        <div className="chart-panel">
          <CandleChart
            symbol={chartSymbol}
            priceRef={chartPriceRef}
            startPrice={positions[0] ? parseFloat(positions[0].entryPrice) : undefined}
            livePrice={livePrice}
          />
        </div>

        {/* Order Entry panel */}
        <div className="order-panel">
          <OrderEntry
            onFill={handleFill}
            onSymbolChange={setChartSymbol}
            onFlatten={flattenAll}
            positions={positions}
            liveBid={liveBid}
            liveAsk={liveAsk}
            livePrice={livePrice}
          />
        </div>

        {/* Positions / History panel */}
        <div className="positions-panel">
          {/* Tab bar */}
          <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--border)", background: "rgba(0,0,0,0.15)" }}>
            {[
              { key: "positions", label: `Positions (${positions.length})` },
              { key: "history",   label: `History (${tradeHistory.length})` },
            ].map(t => (
              <button key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: "9px 16px", fontSize: 12, fontWeight: 600,
                  color: tab === t.key ? "var(--green)" : "var(--text3)",
                  borderBottom: tab === t.key ? "2px solid var(--green)" : "2px solid transparent",
                  fontFamily: "var(--font-body)", transition: "all 0.15s",
                }}>
                {t.label}
              </button>
            ))}
            {positions.length > 0 && (
              <button onClick={flattenAll}
                style={{
                  marginLeft: "auto", marginRight: 10,
                  background: "rgba(255,165,2,0.1)", border: "1px solid rgba(255,165,2,0.3)",
                  color: "var(--amber)", borderRadius: 5, padding: "4px 12px",
                  fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-body)",
                  letterSpacing: "0.05em",
                }}>
                ⚡ FLATTEN ALL
              </button>
            )}
            <button onClick={loadData}
              style={{
                marginLeft: positions.length > 0 ? 0 : "auto", marginRight: 10,
                background: "transparent", border: "1px solid var(--border)",
                color: "var(--text3)", borderRadius: 5, padding: "4px 10px",
                fontSize: 11, cursor: "pointer", fontFamily: "var(--font-body)",
              }}>
              ↺ Refresh
            </button>
          </div>

          {/* Positions table */}
          {tab === "positions" && (
            <table className="cf-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Symbol</th><th>Side</th><th>Qty</th>
                  <th>Entry Price</th><th>Current</th>
                  <th>Unreal. P&L</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingPositions && (
                  <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text3)", padding: 20 }}>Loading...</td></tr>
                )}
                {!loadingPositions && positions.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text3)", padding: 20 }}>No open positions</td></tr>
                )}
                {positions.map(p => {
                  const cur   = chartPriceRef.current || parseFloat(p.entryPrice);
                  const qty   = Number(p.quantity);
                  const entry = parseFloat(p.entryPrice);
                  const uPnl  = p.side === 'LONG' ? (cur - entry) * qty : (entry - cur) * qty;
                  return (
                    <tr key={p.id}>
                      <td><span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13 }}>{p.symbol}</span></td>
                      <td>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800,
                          background: p.side === "LONG" ? "rgba(0,229,160,0.15)" : "rgba(255,71,87,0.15)",
                          color: p.side === "LONG" ? "var(--green)" : "var(--red)",
                          letterSpacing: "0.06em",
                        }}>{p.side}</span>
                      </td>
                      <td className="mono" style={{ fontWeight: 600 }}>{p.quantity}</td>
                      <td className="mono">{entry.toFixed(2)}</td>
                      <td className="mono" style={{ color: "var(--text2)" }}>{cur.toFixed(2)}</td>
                      <td className={uPnl >= 0 ? "num-positive" : "num-negative"} style={{ fontWeight: 700 }}>
                        {uPnl >= 0 ? "+" : ""}{fmt.usd(uPnl)}
                      </td>
                      <td>
                        <button
                          onClick={() => closePosition(p)}
                          disabled={closingId === p.id}
                          style={{
                            background: "rgba(255,71,87,0.12)", border: "1px solid rgba(255,71,87,0.25)",
                            color: "var(--red)", borderRadius: 5, padding: "4px 12px",
                            fontSize: 11, fontWeight: 700, cursor: "pointer",
                          }}>
                          {closingId === p.id ? "Closing..." : "Close"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* History table */}
          {tab === "history" && (
            <>
              <div className="export-bar">
                <span style={{ fontSize: 11, color: "var(--text3)" }}>{tradeHistory.length} closed trades</span>
                <button
                  onClick={() => {
                    const rows = [["Symbol","Side","Qty","Entry","Exit","P&L","Time"]].concat(
                      tradeHistory.map(t => [t.symbol, t.side, t.quantity, parseFloat(t.entryPrice).toFixed(2),
                        t.exitPrice ? parseFloat(t.exitPrice).toFixed(2) : '', Number(t.pnl).toFixed(2),
                        t.closedAt ? new Date(t.closedAt).toLocaleString() : ''])
                    );
                    const csv = rows.map(r => r.join(",")).join("\n");
                    const a = document.createElement("a");
                    a.href = "data:text/csv," + encodeURIComponent(csv);
                    a.download = "clearfund_trades.csv";
                    a.click();
                  }}
                  style={{
                    background: "var(--surface2)", border: "1px solid var(--border2)",
                    color: "var(--text2)", borderRadius: 5, padding: "4px 12px",
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>
                  ↓ Export CSV
                </button>
              </div>
              <table className="cf-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr><th>Symbol</th><th>Side</th><th>Qty</th><th>Entry</th><th>Exit</th><th>P&L</th><th>Closed</th></tr>
                </thead>
                <tbody>
                  {tradeHistory.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text3)", padding: 20 }}>No closed trades yet</td></tr>
                  )}
                  {tradeHistory.map(t => (
                    <tr key={t.id}>
                      <td><span style={{ fontFamily: "var(--font-display)", fontWeight: 800 }}>{t.symbol}</span></td>
                      <td>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800,
                          background: t.side === "LONG" ? "rgba(0,229,160,0.15)" : "rgba(255,71,87,0.15)",
                          color: t.side === "LONG" ? "var(--green)" : "var(--red)", letterSpacing: "0.06em",
                        }}>{t.side}</span>
                      </td>
                      <td className="mono">{t.quantity}</td>
                      <td className="mono">{parseFloat(t.entryPrice).toFixed(2)}</td>
                      <td className="mono">{t.exitPrice ? parseFloat(t.exitPrice).toFixed(2) : '—'}</td>
                      <td className={Number(t.pnl) >= 0 ? "num-positive" : "num-negative"} style={{ fontWeight: 700 }}>
                        {Number(t.pnl) >= 0 ? "+" : ""}{fmt.usd(Number(t.pnl))}
                      </td>
                      <td style={{ color: "var(--text2)", fontSize: 11 }}>
                        {t.closedAt ? new Date(t.closedAt).toLocaleTimeString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {toast && <Toast toast={toast}/>}
    </div>
  );
};

// ─── PAYOUTS PAGE ─────────────────────────────────────────────
const PayoutsPage = ({ account, user }) => {
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState("");
  const plan = PLANS[account.plan?.toLowerCase()] || PLANS.velocity;
  const nextPayoutNum  = account.payoutCount + 1;
  const cap            = plan.payoutCap(nextPayoutNum);
  const availableProfit= Math.max(0, account.totalPnl - 0);
  const maxPayout      = Math.min(cap, availableProfit);
  const isEligible     = account.status === "active_funded" && user.kycStatus === "approved" && account.tradingDays >= plan.minTradingDays;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>Payouts</h1>
        <p style={{ color: "var(--text2)", fontSize: 14 }}>Manage your profit withdrawals and track payout history.</p>
      </div>

      <div className="grid-3">
        <div className="metric-card">
          <div className="metric-label" style={{ marginBottom: 6 }}>Next Payout Cap</div>
          <div className="metric-value" style={{ color: "var(--green)" }}>{cap === Infinity ? "Unlimited" : fmt.usd(cap)}</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Payout #{nextPayoutNum} · {plan.name} Plan</div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{ marginBottom: 6 }}>Available Profit</div>
          <div className="metric-value">{fmt.usd(availableProfit)}</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Total realized P&L</div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{ marginBottom: 6 }}>KYC Status</div>
          <div style={{ marginTop: 4 }}><span className={`badge ${kycMeta[user.kycStatus]?.badge}`}>{kycMeta[user.kycStatus]?.label}</span></div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>{user.kycStatus === "approved" ? "Cleared for payouts" : "Required before payout"}</div>
        </div>
      </div>

      {account.plan === "velocity" && (
        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Velocity Payout Schedule</div>
          <div style={{ display: "flex", gap: 0 }}>
            {[
              { range: "Payouts 1–2", cap: "$3,000",   current: nextPayoutNum <= 2 },
              { range: "Payouts 3–5", cap: "$6,000",   current: nextPayoutNum >= 3 && nextPayoutNum <= 5 },
              { range: "Payouts 6+",  cap: "Unlimited", current: nextPayoutNum >= 6 },
            ].map((tier, i) => (
              <div key={i} style={{
                flex: 1, padding: "14px 16px",
                background: tier.current ? "var(--green-dim)" : "var(--surface2)",
                border: `1px solid ${tier.current ? "rgba(0,229,160,0.3)" : "var(--border)"}`,
                borderRadius: i === 0 ? "var(--radius) 0 0 var(--radius)" : i === 2 ? "0 var(--radius) var(--radius) 0" : "0",
                borderLeft: i > 0 ? "none" : undefined,
              }}>
                <div style={{ fontSize: 12, color: tier.current ? "var(--green)" : "var(--text3)", fontWeight: 600 }}>{tier.range}</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: tier.current ? "var(--green)" : "var(--text)", marginTop: 4 }}>{tier.cap}</div>
                {tier.current && <div style={{ fontSize: 10, color: "var(--green)", marginTop: 4 }}>← You are here</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Request Payout</div>
        {!isEligible ? (
          <div style={{ padding: "14px 16px", background: "var(--amber-dim)", borderRadius: "var(--radius)", border: "1px solid rgba(255,165,2,0.2)" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Icon name="alert" color="var(--amber)" size={16}/>
              <div>
                <div style={{ fontWeight: 600, color: "var(--amber)", marginBottom: 4 }}>Payout Not Yet Available</div>
                <div style={{ fontSize: 13, color: "var(--text2)" }}>
                  {user.kycStatus !== "approved" && <div>· KYC verification required</div>}
                  {account.tradingDays < plan.minTradingDays && <div>· Minimum {plan.minTradingDays} trading days required (you have {account.tradingDays})</div>}
                  {account.status !== "active_funded" && <div>· Account must be in Funded Active status</div>}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 20, alignItems: "flex-end" }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Payout Amount (max {fmt.usd(maxPayout)})</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={`Up to ${fmt.usd(maxPayout)}`} max={maxPayout}/>
            </div>
            <div className="form-group">
              <label className="form-label">Method</label>
              <select style={{ width: 160 }}>
                <option>Bank Transfer</option>
                <option>USDC</option>
                <option>Bitcoin</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={() => setShowModal(true)} disabled={!amount || parseFloat(amount) <= 0 || parseFloat(amount) > maxPayout}>
              Request Payout
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Payout History</div>
        <table className="cf-table">
          <thead><tr><th>Payout #</th><th>Amount</th><th>Status</th><th>Requested</th><th>Paid</th></tr></thead>
          <tbody>
            {mockPayouts.map(p => (
              <tr key={p.id}>
                <td className="mono">#{p.payoutNum}</td>
                <td style={{ fontWeight: 700, color: "var(--green)" }}>{fmt.usd(p.amount)}</td>
                <td><span className={`badge ${p.status === "paid" ? "badge-green" : p.status === "pending" ? "badge-amber" : "badge-blue"}`}>{p.status}</span></td>
                <td style={{ color: "var(--text2)" }}>{fmt.date(p.requested)}</td>
                <td style={{ color: "var(--text2)" }}>{p.paid ? fmt.date(p.paid) : "—"}</td>
              </tr>
            ))}
            {mockPayouts.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text3)", padding: 20 }}>No payouts yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Confirm Payout Request</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              {[
                ["Amount",      fmt.usd(parseFloat(amount) || 0)],
                ["Payout #",   `#${nextPayoutNum}`],
                ["Cap Applied", fmt.usd(cap)],
                ["Plan",        plan.name],
                ["Method",      "Bank Transfer"],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ color: "var(--text2)", fontSize: 14 }}>{k}</span>
                  <span style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>{v}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16, lineHeight: 1.6 }}>
              By confirming, this payout will enter the admin review queue. Approved payouts are typically processed within 1–3 business days.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary btn-full" onClick={() => { setShowModal(false); setAmount(""); }}>Confirm Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── KYC PAGE ─────────────────────────────────────────────────
const KYCPage = ({ user }) => {
  const statusInfo = kycMeta[user.kycStatus];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 640 }} className="fade-in">
      <div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>Identity Verification (KYC)</h1>
        <p style={{ color: "var(--text2)", fontSize: 14 }}>Required before your first payout. Usually takes 5–15 minutes.</p>
      </div>
      <div className="card">
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--green-dim)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="kyc" color="var(--green)" size={22}/>
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, marginBottom: 2 }}>Verification Status</div>
            <span className={`badge ${statusInfo.badge}`}>{statusInfo.label}</span>
          </div>
        </div>
        {[
          { step: 1, label: "Personal Information", desc: "Name, address, date of birth",                             done: user.kycStatus !== "not_started" },
          { step: 2, label: "Government ID",         desc: "Passport, driver's license, or national ID",             done: user.kycStatus === "approved" || user.kycStatus === "submitted" },
          { step: 3, label: "Selfie Verification",   desc: "Quick photo to confirm your identity",                   done: user.kycStatus === "approved" },
          { step: 4, label: "Review",                desc: "Our compliance team reviews your submission (1–24 hours)",done: user.kycStatus === "approved" },
        ].map(s => (
          <div key={s.step} style={{ display: "flex", gap: 14, marginBottom: 16, alignItems: "flex-start" }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
              background: s.done ? "var(--green)" : "var(--surface2)",
              border: `2px solid ${s.done ? "var(--green)" : "var(--border)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, color: s.done ? "#000" : "var(--text3)",
            }}>
              {s.done ? <Icon name="check" color="#000" size={14}/> : s.step}
            </div>
            <div style={{ paddingTop: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{s.label}</div>
              <div style={{ fontSize: 12, color: "var(--text2)" }}>{s.desc}</div>
            </div>
          </div>
        ))}
        {user.kycStatus === "not_started" && (
          <button className="btn btn-primary btn-full" style={{ marginTop: 8 }}>
            <Icon name="shield" size={15}/> Start Verification
          </button>
        )}
        {user.kycStatus === "approved" && (
          <div style={{ background: "var(--green-dim)", borderRadius: "var(--radius)", padding: "12px 16px", display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
            <Icon name="check" color="var(--green)" size={16}/>
            <span style={{ color: "var(--green)", fontWeight: 600 }}>Identity verified. You are cleared for payouts.</span>
          </div>
        )}
        {user.kycStatus === "submitted" && (
          <div style={{ background: "var(--amber-dim)", borderRadius: "var(--radius)", padding: "12px 16px", display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
            <div className="dot-pulse dot-amber"/>
            <span style={{ color: "var(--amber)", fontWeight: 600 }}>Under review — typically 1–24 hours</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── ADMIN: OVERVIEW ──────────────────────────────────────────
const AdminOverview = () => {
  const metrics = [
    { label: "Total Evaluations", value: "1,247", change: "+84 this week",   up: true },
    { label: "Active Funded",     value: "312",   change: "+23 this week",   up: true },
    { label: "Pass Rate",         value: "24.1%", change: "+1.8%",           up: true },
    { label: "Pending Payouts",   value: "18",    change: "Requires review", up: false },
    { label: "Revenue (MTD)",     value: "$214,830", change: "+18% MoM",     up: true },
    { label: "Chargeback Risk",   value: "2.3%",  change: "Monitor",         up: false },
    { label: "KYC Pending",       value: "47",    change: "Avg 6h turnaround",up: true },
    { label: "Active Breaches",   value: "8",     change: "Today",           up: false },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>Admin Dashboard</h1>
        <p style={{ color: "var(--text2)", fontSize: 14 }}>Platform operations overview — ClearFund Trading</p>
      </div>
      <div className="grid-4">
        {metrics.map(m => (
          <div key={m.label} className="metric-card">
            <div className="metric-label" style={{ marginBottom: 6 }}>{m.label}</div>
            <div className="metric-value" style={{ fontSize: 22 }}>{m.value}</div>
            <div className={`metric-change ${m.up ? "metric-up" : "metric-down"}`} style={{ marginTop: 4 }}>{m.change}</div>
          </div>
        ))}
      </div>
      <div className="grid-2">
        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 16 }}>Plan Mix</div>
          {[
            { plan: "Velocity", pct: 58, count: 724, color: "var(--green)" },
            { plan: "Core",     pct: 42, count: 523, color: "var(--blue)"  },
          ].map(p => (
            <div key={p.plan} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>{p.plan}</span>
                <span className="mono" style={{ fontSize: 12, color: "var(--text2)" }}>{p.count} evals ({p.pct}%)</span>
              </div>
              <div className="progress-track"><div className="progress-fill" style={{ width: `${p.pct}%`, background: p.color }}/></div>
            </div>
          ))}
        </div>
        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 16 }}>Account Size Mix</div>
          {[
            { size: "$50K",  pct: 45, count: 561, color: "var(--green)" },
            { size: "$100K", pct: 38, count: 474, color: "var(--amber)" },
            { size: "$150K", pct: 17, count: 212, color: "var(--blue)"  },
          ].map(p => (
            <div key={p.size} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>{p.size}</span>
                <span className="mono" style={{ fontSize: 12, color: "var(--text2)" }}>{p.count} ({p.pct}%)</span>
              </div>
              <div className="progress-track"><div className="progress-fill" style={{ width: `${p.pct}%`, background: p.color }}/></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── ADMIN: USER MANAGEMENT ───────────────────────────────────
const AdminUsers = () => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const filtered = mockAdminUsers.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="page-title">User Management</h1>
        <button className="btn btn-primary btn-sm"><Icon name="plus" size={14}/> Create Account</button>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 340 }}/>
        <select style={{ width: 140 }}><option>All Status</option><option>Funded</option><option>Evaluation</option><option>Breached</option></select>
        <select style={{ width: 120 }}><option>All Plans</option><option>Velocity</option><option>Core</option></select>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table className="cf-table">
          <thead><tr><th>Trader</th><th>Plan</th><th>Size</th><th>Balance</th><th>Status</th><th>KYC</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setSelected(u === selected ? null : u)}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>{u.email}</div>
                </td>
                <td><span className={`badge ${u.plan === "velocity" ? "badge-green" : "badge-blue"}`}>{u.plan}</span></td>
                <td className="mono" style={{ fontSize: 12 }}>{u.size}</td>
                <td className="mono">{fmt.usd(u.balance)}</td>
                <td><span className={`badge ${statusMeta[u.status]?.badge}`}>{statusMeta[u.status]?.label}</span></td>
                <td><span className={`badge ${kycMeta[u.kyc]?.badge}`}>{kycMeta[u.kyc]?.label}</span></td>
                <td>
                  <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-xs"><Icon name="eye" size={12}/></button>
                    <button className="btn btn-ghost btn-xs"><Icon name="settings" size={12}/></button>
                    {u.status === "active_funded" && <button className="btn btn-danger btn-xs">Breach</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 14 }}>{selected.name} — Account Detail</div>
          <div className="grid-3">
            {[
              ["Email",   selected.email],
              ["Plan",    selected.plan],
              ["Size",    selected.size],
              ["Balance", fmt.usd(selected.balance)],
              ["Status",  statusMeta[selected.status]?.label],
              ["KYC",     kycMeta[selected.kyc]?.label],
            ].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{k}</div>
                <div style={{ fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="divider"/>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-ghost btn-sm"><Icon name="refresh" size={13}/> Reset Account</button>
            <button className="btn btn-ghost btn-sm"><Icon name="lock" size={13}/> Suspend</button>
            <button className="btn btn-danger btn-sm">Force Breach</button>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }}>View Full Audit Log</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ADMIN: PAYOUT REVIEW ─────────────────────────────────────
const AdminPayouts = () => {
  const [payouts, setPayouts] = useState(mockPayoutQueue);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const approve = (id) => { setPayouts(ps => ps.filter(p => p.id !== id)); setSelected(null); };
  const reject  = (id) => { setPayouts(ps => ps.filter(p => p.id !== id)); setSelected(null); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="page-title">Payout Review Queue</h1>
        <span className="badge badge-amber">{payouts.length} Pending</span>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table className="cf-table">
          <thead><tr><th>Trader</th><th>Plan</th><th>Amount</th><th>Payout #</th><th>Cap</th><th>KYC</th><th>Risk</th><th>Requested</th><th></th></tr></thead>
          <tbody>
            {payouts.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--text3)", padding: 24 }}>Queue is empty</td></tr>}
            {payouts.map(p => (
              <tr key={p.id}>
                <td><div style={{ fontWeight: 600, fontSize: 13 }}>{p.trader}</div><div style={{ fontSize: 11, color: "var(--text3)" }}>{p.email}</div></td>
                <td><span className={`badge ${p.plan === "velocity" ? "badge-green" : "badge-blue"}`}>{p.plan}</span></td>
                <td className="mono" style={{ fontWeight: 700, color: "var(--green)" }}>{fmt.usd(p.amount)}</td>
                <td className="mono">#{p.payoutNum}</td>
                <td className="mono">{fmt.usd(p.cap)}</td>
                <td><span className={`badge ${kycMeta[p.kyc]?.badge}`}>{kycMeta[p.kyc]?.label}</span></td>
                <td><span className={`badge ${p.risk === "low" ? "badge-green" : "badge-amber"}`}>{p.risk}</span></td>
                <td style={{ color: "var(--text2)", fontSize: 12 }}>{fmt.date(p.requested)}</td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-primary btn-xs" onClick={() => approve(p.id)}>Approve</button>
                    <button className="btn btn-danger btn-xs" onClick={() => reject(p.id)}>Reject</button>
                    <button className="btn btn-ghost btn-xs" onClick={() => setSelected(selected?.id === p.id ? null : p)}>Details</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, marginBottom: 14 }}>Payout Detail — {selected.trader}</div>
          <div className="grid-2" style={{ marginBottom: 16 }}>
            {[
              ["Requested Amount",    fmt.usd(selected.amount)],
              ["Payout Cap",          fmt.usd(selected.cap)],
              ["Amount Within Cap?",  selected.amount <= selected.cap ? "✓ Yes" : "⚠ Exceeds cap"],
              ["KYC Status",          kycMeta[selected.kyc]?.label],
              ["Risk Level",          selected.risk],
              ["Payout Number",       `#${selected.payoutNum}`],
            ].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{k}</div>
                <div style={{ fontWeight: 600 }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Internal Note (logged)</label>
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Add review note..."/>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary" onClick={() => approve(selected.id)}><Icon name="check" size={14}/> Approve & Process</button>
            <button className="btn btn-danger"  onClick={() => reject(selected.id)}><Icon name="x" size={14}/> Reject</button>
            <button className="btn btn-ghost">Hold for Review</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ADMIN: CONFIGURATION ─────────────────────────────────────
const AdminConfig = () => {
  const [drawdownRule,      setDrawdownRule]      = useState("B");
  const [profitSplitStart,  setProfitSplitStart]  = useState(3);
  const [traderSplit,       setTraderSplit]        = useState(80);
  const [payoutWindow,      setPayoutWindow]       = useState(5);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>Platform Configuration</h1>
        <p style={{ color: "var(--text2)", fontSize: 14 }}>Manage plan rules, pricing, and payout logic.</p>
      </div>
      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 16 }}>Evaluation Pricing</div>
        <table className="cf-table">
          <thead><tr><th>Plan</th><th>$50K</th><th>$100K</th><th>$150K</th></tr></thead>
          <tbody>
            {[
              { plan: "Core",     prices: [99,  139, 179] },
              { plan: "Velocity", prices: [159, 229, 269] },
            ].map(row => (
              <tr key={row.plan}>
                <td style={{ fontWeight: 600 }}>{row.plan}</td>
                {row.prices.map((p, i) => <td key={i}><input type="number" defaultValue={p} style={{ width: 80 }}/></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 4 }}>Drawdown Reset Logic</div>
        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 14 }}>Determines how drawdown limits are recalculated after a payout is processed.</p>
        {[
          { key: "A", label: "Option A — Hard Reset to Zero Buffer",           desc: "After payout, the account's allowable drawdown buffer becomes $0. Any subsequent loss breaches immediately. Very strict." },
          { key: "B", label: "Option B — Trailing Drawdown Reset (Recommended)",desc: "After payout, the trailing drawdown floor is recalculated based on the new balance. Standard industry practice." },
        ].map(opt => (
          <div key={opt.key} style={{
            padding: "14px 16px", borderRadius: "var(--radius)", marginBottom: 10,
            border: `1px solid ${drawdownRule === opt.key ? "rgba(0,229,160,0.4)" : "var(--border)"}`,
            background: drawdownRule === opt.key ? "var(--green-dim)" : "var(--surface2)",
            cursor: "pointer",
          }} onClick={() => setDrawdownRule(opt.key)}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${drawdownRule === opt.key ? "var(--green)" : "var(--border)"}`, background: drawdownRule === opt.key ? "var(--green)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, flexShrink: 0 }}>
                {drawdownRule === opt.key && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#000" }}/>}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: "var(--text2)" }}>{opt.desc}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 16 }}>Payout Configuration</div>
        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Payout Window (days)</label>
            <input type="number" value={payoutWindow} onChange={e => setPayoutWindow(e.target.value)}/>
          </div>
          <div className="form-group">
            <label className="form-label">Profit Split Starts at Payout #</label>
            <input type="number" value={profitSplitStart} onChange={e => setProfitSplitStart(e.target.value)} min="1" max="10"/>
          </div>
          <div className="form-group">
            <label className="form-label">Trader Split % (after threshold)</label>
            <input type="number" value={traderSplit} onChange={e => setTraderSplit(e.target.value)} min="50" max="100"/>
          </div>
        </div>
        <div style={{ marginTop: 14, padding: "10px 14px", background: "var(--surface2)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--text2)" }}>
          Current config: 100% to trader for payouts 1–{profitSplitStart - 1}, then {traderSplit}/{100 - traderSplit} split from payout #{profitSplitStart} onward.
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-primary"><Icon name="check" size={14}/> Save Configuration</button>
        <button className="btn btn-ghost">Reset to Defaults</button>
      </div>
    </div>
  );
};

// ─── FAQ ACCORDION ITEM ───────────────────────────────────────
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--border)", overflow: "hidden" }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", background: "none", border: "none", color: "var(--text)", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 0", cursor: "pointer", gap: 16, textAlign: "left" }}
      >
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 15 }}>{q}</span>
        <span style={{ fontSize: 18, color: "var(--green)", flexShrink: 0, transform: open ? "rotate(45deg)" : "rotate(0)", transition: "transform 0.2s" }}>+</span>
      </button>
      {open && <div style={{ paddingBottom: 18, fontSize: 14, color: "var(--text2)", lineHeight: 1.8 }}>{a}</div>}
    </div>
  );
};

// ─── LANDING PAGE ─────────────────────────────────────────────
const LandingPage = ({ onLogin, onCheckout, onUser, onDashboard, onLegal }) => {
  const [selectedPlan, setSelectedPlan] = useState("velocity");
  const [selectedSize, setSelectedSize] = useState("100K");
  const [tickerItems, setTickerItems] = useState([
    { symbol: "NQ",  price: "—", change: "—", up: true },
    { symbol: "ES",  price: "—", change: "—", up: true },
    { symbol: "YM",  price: "—", change: "—", up: true },
    { symbol: "GC",  price: "—", change: "—", up: true },
    { symbol: "CL",  price: "—", change: "—", up: true },
    { symbol: "RTY", price: "—", change: "—", up: true },
  ]);

  const plan  = PLANS[selectedPlan];
  const price = plan.prices[selectedSize];

  const stats = [
    { value: "$150K",  label: "Max Capital" },
    { value: "$0",     label: "Monthly Fees" },
    { value: "5 Days", label: "Payout Window" },
    { value: "100%",   label: "Rule Transparency" },
  ];

  useEffect(() => {
    const finnhubSymbols = {
      NQ:  'OANDA:NAS100_USD',
      ES:  'OANDA:SPX500_USD',
      YM:  'OANDA:US30_USD',
      GC:  'OANDA:XAU_USD',
      CL:  'OANDA:BCO_USD',
      RTY: 'OANDA:US2000_USD',
    };

    const fetchSnapshots = async () => {
      await Promise.all(Object.entries(finnhubSymbols).map(async ([displaySymbol, sym]) => {
        try {
          const res  = await fetch(`https://finnhub.io/api/v1/quote?symbol=${sym}&token=d6jgt59r01qkvh5pred0d6jgt59r01qkvh5predg`);
          const data = await res.json();
          if (data.c) {
            const changePct = (data.dp || 0).toFixed(2);
            setTickerItems(prev => prev.map(item =>
              item.symbol === displaySymbol ? {
                symbol: displaySymbol,
                price:  data.c.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                change: `${changePct >= 0 ? '+' : ''}${changePct}%`,
                up:     changePct >= 0,
              } : item
            ));
          }
        } catch (e) { console.error('Quote fetch failed', e); }
      }));
    };

    fetchSnapshots();

    const ws = new WebSocket('wss://ws.finnhub.io?token=d6jgt59r01qkvh5pred0d6jgt59r01qkvh5predg');
    ws.onopen = () => {
      Object.values(finnhubSymbols).forEach(sym => ws.send(JSON.stringify({ type: 'subscribe', symbol: sym })));
    };
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'trade' && data.data) {
        const trade = data.data[0];
        const displaySymbol = Object.keys(finnhubSymbols).find(k => finnhubSymbols[k] === trade.s);
        if (displaySymbol) {
          setTickerItems(prev => prev.map(item => {
            if (item.symbol === displaySymbol) {
              const prevPrice = parseFloat(item.price.replace(',', '')) || trade.p;
              const changePct = ((trade.p - prevPrice) / prevPrice * 100).toFixed(2);
              return {
                symbol: displaySymbol,
                price:  trade.p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                change: `${changePct >= 0 ? '+' : ''}${changePct}%`,
                up:     changePct >= 0,
              };
            }
            return item;
          }));
        }
      }
    };
    ws.onerror = (err) => console.error('Finnhub WS error:', err);
    return () => ws.close();
  }, []);

  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em" }}>
          <span style={{ color: "var(--green)" }}>Clear</span>Fund
        </div>
        <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
          {[["Pricing","pricing"],["Rules","rules"],["FAQ","faq"],["About","about"]].map(([label,id]) => (
            <span key={id} style={{ fontSize: 14, color: "var(--text2)", cursor: "pointer", transition: "color 0.15s" }}
              onMouseOver={e => e.target.style.color = "var(--text)"}
              onMouseOut={e  => e.target.style.color = "var(--text2)"}
              onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
            >{label}</span>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {onUser ? (
            <>
              <span style={{ fontSize: 13, color: "var(--text2)", alignSelf: "center" }}>{onUser.email}</span>
              <button className="btn btn-primary btn-sm" onClick={onDashboard}>Dashboard</button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost btn-sm" onClick={onLogin}>Sign In</button>
              <button className="btn btn-primary btn-sm" onClick={() => window.location.href = '/register'}>Get Funded</button>
            </>
          )}
        </div>
      </nav>

      {/* Ticker */}
      <div className="ticker-tape" style={{ marginTop: 56 }}>
        <div className="ticker-inner">
          {[...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text2)" }}>
              <span style={{ color: "var(--text)", fontWeight: 600 }}>{t.symbol}</span>
              {" "}{t.price}{" "}
              <span style={{ color: t.up ? "var(--green)" : "var(--red)" }}>{t.change}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg"/>
        <div className="hero-grid"/>
        <div style={{ position: "relative", zIndex: 1, maxWidth: 800, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "var(--green-dim)", border: "1px solid rgba(0,229,160,0.2)", borderRadius: 100, padding: "5px 14px", marginBottom: 28 }}>
            <span className="dot-pulse"/>
            <span style={{ fontSize: 12, color: "var(--green)", fontWeight: 600, fontFamily: "var(--font-mono)" }}>LIVE PLATFORM — APPLICATIONS OPEN</span>
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(38px, 7vw, 72px)", lineHeight: 1.05, letterSpacing: "-0.03em", marginBottom: 24 }}>
            Trade with{" "}
            <span style={{ color: "var(--green)", position: "relative" }}>
              firm capital.
              <svg style={{ position: "absolute", bottom: -8, left: 0, width: "100%" }} viewBox="0 0 200 10" preserveAspectRatio="none" height="6">
                <path d="M0,8 Q100,-4 200,8" stroke="var(--green)" strokeWidth="2" fill="none" opacity="0.4"/>
              </svg>
            </span>
            <br/>Keep your profits.
          </h1>
          <p style={{ fontSize: "clamp(15px, 2vw, 18px)", color: "var(--text2)", maxWidth: 540, margin: "0 auto 36px", lineHeight: 1.7 }}>
            Pass our evaluation. Get funded up to $150K. Withdraw profits with transparent rules and no hidden conditions.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary" style={{ padding: "14px 32px", fontSize: 16 }} onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>
              <Icon name="zap" size={16}/> Start Evaluation from $99
            </button>
            <button className="btn btn-ghost" style={{ padding: "14px 32px", fontSize: 16 }}>View Rules</button>
          </div>
          <div style={{ display: "flex", gap: 40, justifyContent: "center", marginTop: 56 }}>
            {stats.map(s => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, color: "var(--green)" }}>{s.value}</div>
                <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding: "80px 60px", background: "var(--bg2)", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>Pricing</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 36, letterSpacing: "-0.02em" }}>Choose your path to funding</h2>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 0, marginBottom: 32 }}>
            <div style={{ display: "flex", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 4, gap: 4 }}>
              {["core", "velocity"].map(p => (
                <button key={p} className="btn btn-sm"
                  style={{ textTransform: "capitalize", background: selectedPlan === p ? (p === "velocity" ? "var(--green)" : "var(--blue)") : "transparent", color: selectedPlan === p ? "#000" : "var(--text2)", border: "none" }}
                  onClick={() => setSelectedPlan(p)}
                >{PLANS[p].name}</button>
              ))}
            </div>
          </div>
          <div className="grid-3" style={{ marginBottom: 32 }}>
            {ACCOUNT_SIZES.map(size => {
              const p          = plan.prices[size];
              const isSelected = selectedSize === size;
              return (
                <div key={size} className={`plan-card ${isSelected ? "selected" : ""}`} onClick={() => setSelectedSize(size)}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, marginBottom: 4 }}>${parseInt(size.replace("K",""))}K</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 32, fontWeight: 700, color: "var(--green)", marginBottom: 16 }}>${p}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                      `Profit target: ${fmt.usd(plan.profitTarget[size])}`,
                      `Max drawdown: ${fmt.usd(plan.maxDrawdown[size])}`,
                      `Daily loss: ${fmt.usd(plan.dailyLoss[size])}`,
                      `Payout cap: ${plan.payoutCapLabel}`,
                      `Min trading days: ${plan.minTradingDays}`,
                    ].map(f => (
                      <div key={f} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: isSelected ? "var(--text)" : "var(--text2)" }}>
                        <Icon name="check" color={isSelected ? "var(--green)" : "var(--text3)"} size={13}/>{f}
                      </div>
                    ))}
                  </div>
                  {isSelected && (
                    <button className="btn btn-primary btn-full" style={{ marginTop: 20 }} onClick={() => onCheckout(selectedPlan, size)}>
                      Buy {plan.name} {size} — ${p}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {selectedPlan === "velocity" && (
            <div style={{ background: "var(--green-dim)", border: "1px solid rgba(0,229,160,0.2)", borderRadius: "var(--radius-lg)", padding: "20px 24px", display: "flex", gap: 20, alignItems: "center" }}>
              <Icon name="trending" color="var(--green)" size={28}/>
              <div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, marginBottom: 4 }}>Velocity: Scales to Unlimited Payouts</div>
                <div style={{ fontSize: 13, color: "var(--text2)" }}>
                  Payouts 1–2: $3,000 cap → Payouts 3–5: $6,000 cap → Payout 6+: <strong style={{ color: "var(--green)" }}>Unlimited</strong>. Build trust, unlock your ceiling.
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Rules */}
      <section id="rules" style={{ padding: "80px 60px", background: "var(--bg)", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>Rules</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 36, letterSpacing: "-0.02em" }}>Clear rules. Fixed rules. No surprises.</h2>
            <p style={{ color: "var(--text2)", marginTop: 14, fontSize: 15, maxWidth: 620, margin: "14px auto 0" }}>
              All rules are published, fixed, and strictly enforced as written. No hidden conditions. No discretionary interpretation. Ever.
            </p>
          </div>

          {[
            {
              icon: "zap", iconColor: "var(--blue)", title: "Evaluation Phase Rules", cardColor: "var(--blue)",
              items: [
                { title: "Profit Target",     body: "Each evaluation has a fixed profit target based on account size. Only realized profits count. Violating any risk rule fails the evaluation immediately." },
                { title: "Maximum Drawdown",  body: "Strict drawdown limits apply at all times. Surpassing the account-specific drawdown ends the evaluation immediately with no exceptions." },
                { title: "Daily Loss Limit",  body: "Daily loss limits are enforced and include all P&L. Breaching the limit results in evaluation failure. Limits reset each trading day at market open." },
                { title: "Consistency Rule",  body: "No single trading day may account for a disproportionate share of total profits. This rule promotes disciplined trading and applies only during evaluation." },
                { title: "Minimum Trading Days", body: "A minimum of 2 trading days is required to pass an evaluation. Days do not need to be consecutive. Trade at your own pace." },
                { title: "No Time Limit",     body: "There is no deadline to pass the evaluation. No subscriptions, no forced expirations. Your account remains active until you pass or violate a rule." },
              ],
            },
            {
              icon: "shield", iconColor: "var(--green)", title: "Funded Account Rules", cardColor: "var(--green)",
              items: [
                { title: "What Changes When Funded", body: "No profit target. No consistency rule. Evaluation-only restrictions are fully lifted once funded. You trade under a clean, stable rule set." },
                { title: "Ongoing Risk Rules",       body: "Maximum drawdown, daily loss limits, position limits, and platform compliance remain. Funded traders are assessed solely on drawdown and risk management." },
                { title: "Payout Eligibility",       body: "You must be within all drawdown and risk limits at time of request. Minimum thresholds apply. KYC verification required before first payout." },
                { title: "Scaling Rules",            body: "All scaling policies are published and performance-based. No discretionary or retroactive scaling adjustments are ever made." },
              ],
            },
            {
              icon: "dollar", iconColor: "var(--amber)", title: "Payout Rules", cardColor: "var(--amber)",
              items: [
                { title: "Payout Frequency",    body: "Payouts are available every 5 days. Requests outside payout windows are not accepted." },
                { title: "Processing Time",     body: "Initiated within 24-48 hours after compliance review. Available via fiat or crypto. Holiday or compliance delays do not affect eligibility." },
                { title: "Payout Caps",         body: "Caps apply early and increase with your trading record. Once thresholds are met, no caps apply. All caps are published in advance." },
                { title: "Prohibited Activity", body: "Trade copying, account sharing, hedging across accounts, latency abuse, and automated bypass systems result in immediate disqualification and permanent closure." },
              ],
            },
          ].map(section => (
            <div key={section.title} style={{ marginBottom: 48 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{ width: 32, height: 32, borderRadius: "var(--radius)", background: `${section.iconColor}22`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={section.icon} color={section.iconColor} size={16}/>
                </div>
                <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20 }}>{section.title}</h3>
              </div>
              <div className="grid-2" style={{ gap: 16 }}>
                {section.items.map(r => (
                  <div key={r.title} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "18px 20px" }}>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, marginBottom: 8, color: section.cardColor }}>{r.title}</div>
                    <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.7 }}>{r.body}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: "var(--radius-lg)", padding: "24px 28px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 10 }}>Enforcement & Transparency</div>
            <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.8, marginBottom: 12 }}>
              All rules are enforced precisely as published. No retroactive changes, hidden conditions, or discretionary denials. Violations are communicated with written justifications. Every participant is subject to the same standards.
            </p>
            <p style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.8 }}>
              <strong style={{ color: "var(--text2)" }}>Risk Disclosure:</strong> Trading is inherently risky. Most traders will not become profitable. This program is designed to support disciplined, risk-managed trading. Final interpretation of all rules is reserved for ClearFund Trading Risk Management.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: "80px 60px", background: "var(--bg2)", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>FAQ</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 36, letterSpacing: "-0.02em" }}>Clear answers. No fine print.</h2>
          </div>
          {[
            { q: "Are there monthly fees or subscriptions?",                      a: "No. All evaluation accounts are one-time fees only. There are no monthly charges, no recurring billing, and no forced expirations." },
            { q: "Does the consistency rule apply after I am funded?",            a: "No. Consistency rules apply only during the evaluation phase. Once funded, you trade under a fixed, stable rule set with no profit composition restrictions." },
            { q: "How long do payouts take to process?",                          a: "Payouts are processed after compliance review. Most payouts are initiated within 48 hours. Withdrawals are available via fiat or crypto and are subject to eligibility requirements." },
            { q: "Is there a time limit to pass the evaluation?",                 a: "No. Take as long as you need. Trade at your own pace. Your evaluation never expires." },
            { q: "What happens if I break a rule during evaluation?",             a: "The evaluation fails. You may reset the account for a fee and start fresh with the original balance and rules intact." },
            { q: "How is ClearFund different from subscription-based firms?",     a: "Most firms require monthly fees just to stay active. ClearFund charges once for evaluations — no renewals, no forced billing, no expiring access." },
            { q: "Do other firms enforce consistency rules after funding?",        a: "Many firms continue hidden or discretionary consistency rules after funding. ClearFund does not. Once funded, evaluation-only rules are fully removed." },
            { q: "Are payouts limited by how I generate profits?",                a: "No. ClearFund limits payouts by payout count, not profit composition. We reward consistency without hidden constraints. All caps are published in advance." },
            { q: "Do rules ever change mid-account?",                             a: "No. ClearFund rules are fixed, published, and never changed mid-account. What you sign up for is what you trade under — no mid-account surprises, ever." },
          ].map((item, i) => <FaqItem key={i} q={item.q} a={item.a}/>)}
          <div style={{ marginTop: 48, background: "var(--green-dim)", border: "1px solid rgba(0,229,160,0.2)", borderRadius: "var(--radius-lg)", padding: "24px 28px", textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Trust in Every Detail</div>
            <p style={{ color: "var(--text2)", fontSize: 14, maxWidth: 500, margin: "0 auto" }}>
              Every rule is published, enforced consistently, and designed for you to succeed in our simulated evaluation environment.
            </p>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" style={{ padding: "80px 60px", background: "var(--bg)", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>About</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 36, letterSpacing: "-0.02em" }}>Built for serious traders.</h2>
            <p style={{ color: "var(--text2)", marginTop: 14, fontSize: 15, maxWidth: 580, margin: "14px auto 0" }}>
              ClearFund Trading is a proprietary trading evaluation firm built on one principle: absolute transparency. We fund disciplined traders with clear rules, fixed conditions, and no hidden agenda.
            </p>
          </div>
          <div className="grid-3" style={{ marginBottom: 48 }}>
            {[
              { icon: "shield",   color: "var(--green)", title: "Fixed Rules",        body: "Every rule is published before you buy. Nothing changes mid-account. No retroactive enforcement. No discretionary decisions." },
              { icon: "zap",      color: "var(--blue)",  title: "Two Clear Plans",    body: "Core for consistent capped growth. Velocity for traders who want to scale to unlimited payouts. No confusion, no upsells." },
              { icon: "dollar",   color: "var(--amber)", title: "Real Payouts",       body: "Funded traders receive payouts every 5 days, processed within 48 hours. Available in fiat or crypto. Transparent caps that scale with your track record." },
              { icon: "globe",    color: "var(--green)", title: "No Subscriptions",   body: "One-time evaluation fee. No monthly charges. No expiring access. Your account stays active until you pass or violate a rule." },
              { icon: "trending", color: "var(--blue)",  title: "Scalable Funding",   body: "Velocity plan traders unlock unlimited payouts from payout 6 onward. We grow with our best traders, not against them." },
              { icon: "lock",     color: "var(--amber)", title: "Trader Privacy",     body: "Your data is never sold or disclosed to third parties. Used strictly for risk management, compliance, and platform operations." },
            ].map(card => (
              <div key={card.title} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "22px" }}>
                <div style={{ width: 40, height: 40, borderRadius: "var(--radius)", background: `${card.color}22`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                  <Icon name={card.icon} color={card.color} size={18}/>
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{card.title}</div>
                <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.7 }}>{card.body}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <button className="btn btn-primary" style={{ padding: "14px 36px", fontSize: 16 }} onClick={onLogin}>
              <Icon name="zap" size={16}/> Start Your Evaluation
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: "40px 60px", borderTop: "1px solid var(--border)", background: "var(--bg)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18 }}>
            <span style={{ color: "var(--green)" }}>Clear</span>Fund<span style={{ color: "var(--text3)", fontSize: 12, fontWeight: 400, marginLeft: 8 }}>Trading</span>
          </div>
          <div style={{ display: "flex", gap: 24 }}>
            {[
              { label: "Terms of Service", key: "terms" },
              { label: "Risk Disclosure",  key: "risk" },
              { label: "Privacy Policy",   key: "privacy" },
              { label: "Refund Policy",    key: "refund" },
              { label: "KYC / AML",        key: "kyc_aml" },
            ].map(l => (
              <span key={l.key} onClick={() => onLegal(l.key)}
                style={{ fontSize: 12, color: "var(--text3)", cursor: "pointer", transition: "color 0.15s" }}
                onMouseOver={e => e.target.style.color = "var(--green)"}
                onMouseOut={e  => e.target.style.color = "var(--text3)"}
              >{l.label}</span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--text3)" }}>© 2025 ClearFund Trading</div>
        </div>
        <div style={{ maxWidth: 1000, margin: "20px auto 0", fontSize: 11, color: "var(--text3)", lineHeight: 1.7 }}>
          Trading futures involves substantial risk of loss. ClearFund Trading is a simulated trading environment. Funded accounts trade on a simulated basis. Past performance is not indicative of future results. All evaluations are conducted in a simulated environment.
        </div>
      </footer>
    </div>
  );
};

// ─── AUTH MODAL ───────────────────────────────────────────────
const AuthModal = ({ onClose, onAuth }) => {
  const [mode, setMode]       = useState("login");
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const data = await auth.login({ email, password });
      setLoading(false);
      onAuth({ role: data.user.role.toLowerCase() === 'admin' ? 'admin' : 'trader', email: data.user.email, ...data.user });
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Login failed. Please check your credentials.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 24, marginBottom: 4 }}>
          <span style={{ color: "var(--green)" }}>Clear</span>Fund
        </div>
        <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 24 }}>
          {mode === "login" ? "Sign in to your account" : "Create your account"}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {error && (
            <div style={{ background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#FF4757' }}>
              ⚠ {error}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"/>
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"/>
          </div>
          {mode === "login" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", background: "var(--surface2)", borderRadius: "var(--radius)", fontSize: 13 }}>
              <input type="checkbox" id="adminToggle" checked={isAdmin} onChange={e => {
                setIsAdmin(e.target.checked);
                if (e.target.checked) { setEmail('admin@clearfundtrading.com'); setPassword('admin123'); }
                else { setEmail(''); setPassword(''); }
              }} style={{ width: "auto" }}/>
              <label htmlFor="adminToggle" style={{ cursor: "pointer", color: "var(--text2)" }}>Demo as Admin user</label>
            </div>
          )}
          <button className="btn btn-primary btn-full" onClick={submit} disabled={loading}>
            {loading ? "Signing in..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>
          {mode === "login" ? (
            <div style={{ textAlign: "center", fontSize: 13, color: "var(--text2)", display: "flex", flexDirection: "column", gap: 8 }}>
              <span>Don't have an account?{" "}<span style={{ color: "var(--green)", cursor: "pointer" }} onClick={() => window.location.href = '/register'}>Sign up</span></span>
              <span style={{ color: "var(--text3)", cursor: "pointer" }} onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('nav:request-reset')); }}>Forgot password?</span>
            </div>
          ) : (
            <div style={{ textAlign: "center", fontSize: 13, color: "var(--text2)" }}>
              Already have an account?{" "}<span style={{ color: "var(--green)", cursor: "pointer" }} onClick={() => setMode("login")}>Sign in</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── CHECKOUT MODAL ───────────────────────────────────────────
const CheckoutModal = ({ plan, size, price, onClose, onComplete }) => {
  const [cardNum, setCardNum]   = useState("4242 4242 4242 4242");
  const [expiry, setExpiry]     = useState("12/27");
  const [cvv, setCvv]           = useState("123");
  const [processing, setProcessing] = useState(false);

  const complete = () => {
    setProcessing(true);
    setTimeout(() => { setProcessing(false); onComplete(); }, 1500);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18 }}>Complete Purchase</div>
          <button className="btn btn-ghost btn-xs" onClick={onClose}>✕</button>
        </div>
        <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: "14px 16px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700 }}>{PLANS[plan]?.name} Plan · ${size}K Account</div>
            <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>One-time evaluation fee</div>
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 24, color: "var(--green)" }}>${price}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Card Number</label>
            <input value={cardNum} onChange={e => setCardNum(e.target.value)} placeholder="0000 0000 0000 0000"/>
          </div>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">Expiry</label><input value={expiry} onChange={e => setExpiry(e.target.value)} placeholder="MM/YY"/></div>
            <div className="form-group"><label className="form-label">CVV</label><input value={cvv} onChange={e => setCvv(e.target.value)} placeholder="123"/></div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              {["VISA", "MC", "AMEX", "Apple Pay"].map(b => <span key={b} className="badge badge-gray" style={{ fontSize: 10 }}>{b}</span>)}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center", fontSize: 11, color: "var(--text3)" }}>
              <Icon name="lock" size={12} color="var(--text3)"/> Secured by Stripe. 256-bit SSL encryption.
            </div>
          </div>
          <div className="divider"/>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
            <span style={{ color: "var(--text2)" }}>Evaluation Fee</span>
            <span className="mono">${price}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700 }}>
            <span>Total</span>
            <span className="mono" style={{ color: "var(--green)" }}>${price}</span>
          </div>
          <button className="btn btn-primary btn-full" style={{ padding: "14px" }} onClick={complete} disabled={processing}>
            {processing ? "Processing..." : `Pay $${price} — Start Evaluation`}
          </button>
          <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "center" }}>
            By purchasing, you agree to our Terms of Service and Risk Disclosure. No refunds after account credentials are provisioned.
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── TRADER SIDEBAR ───────────────────────────────────────────
const TraderSidebar = ({ page, setPage, user, onLogout, account }) => {
  const navItems = [
    { id: "dashboard",   icon: "home",     label: "Dashboard" },
    { id: "trading",     icon: "activity", label: "Trading" },
    { id: "performance", icon: "chart",    label: "Performance" },
    { id: "payouts",     icon: "dollar",   label: "Payouts" },
    { id: "kyc",         icon: "kyc",      label: "Verification" },
    { id: "documents",   icon: "file",     label: "Documents" },
    { id: "support",     icon: "inbox",    label: "Support" },
  ];
  return (
    <div className="cf-sidebar">
      <div style={{ padding: "20px 16px 12px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18 }}>
          <span style={{ color: "var(--green)" }}>Clear</span>Fund
        </div>
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>Trader Portal</div>
      </div>
      <div style={{ margin: "12px 8px", padding: "12px", background: "var(--green-dim)", border: "1px solid rgba(0,229,160,0.2)", borderRadius: "var(--radius)" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div className="dot-pulse"/>
          <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 700 }}>EVALUATION</span>
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 700, marginTop: 6 }}>{fmt.usd(Number(account.balance))}</div>
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>{account.plan?.replace('SIZE_','') || 'Velocity'} · {account.size?.replace('SIZE_','') || '$100K'}</div>
      </div>
      <nav style={{ flex: 1, padding: "8px 0" }}>
        {navItems.map(item => (
          <div key={item.id}
            className={`nav-item ${page === item.id ? "active" : ""}`}
            onClick={() => {
              if (item.id === "trading") {
                window.open("/trade", "_blank", "noopener,noreferrer");
              } else {
                setPage(item.id);
              }
            }}
          >
            <Icon name={item.icon} size={15}/>
            {item.label}
            {item.id === "trading" && (
              <span style={{ marginLeft: "auto", fontSize: 9, color: "var(--text3)", fontFamily: "var(--font-mono)" }}>↗</span>
            )}
            {item.id === "kyc" && user.kycStatus === "approved" && <Icon name="check" size={12} color="var(--green)"/>}
          </div>
        ))}
      </nav>
      <div style={{ padding: "12px 8px", borderTop: "1px solid var(--border)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px", borderRadius: "var(--radius)" }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--green-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "var(--green)" }}>
            {user.name.charAt(0)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</div>
            <div style={{ fontSize: 10, color: "var(--text3)", overflow: "hidden", textOverflow: "ellipsis" }}>{user.email}</div>
          </div>
          <button className="btn btn-ghost" style={{ padding: 6, border: "none", background: "transparent" }} onClick={onLogout}>
            <Icon name="logout" size={14} color="var(--text3)"/>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ADMIN SIDEBAR ────────────────────────────────────────────
const AdminSidebar = ({ page, setPage, onLogout }) => {
  const navItems = [
    { section: "Overview" },
    { id: "admin_overview",  icon: "bar_chart", label: "Dashboard" },
    { section: "Management" },
    { id: "admin_users",     icon: "users",     label: "Users" },
    { id: "admin_accounts",  icon: "file",      label: "Accounts" },
    { id: "admin_payouts",   icon: "dollar",    label: "Payout Review", badge: "18" },
    { section: "Risk" },
    { id: "admin_risk",      icon: "alert",     label: "Risk Monitor" },
    { section: "System" },
    { id: "admin_config",    icon: "settings",  label: "Configuration" },
    { id: "admin_reports",   icon: "chart",     label: "Reports" },
  ];
  return (
    <div className="cf-sidebar">
      <div style={{ padding: "20px 16px 12px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18 }}>
          <span style={{ color: "var(--green)" }}>Clear</span>Fund
        </div>
        <div style={{ fontSize: 10, color: "var(--amber)", marginTop: 2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>⬡ Admin Portal</div>
      </div>
      <nav style={{ flex: 1, padding: "8px 0" }}>
        {navItems.map((item, i) => (
          item.section ? (
            <div key={i} className="nav-section">{item.section}</div>
          ) : (
            <div key={item.id} className={`nav-item ${page === item.id ? "active" : ""}`} onClick={() => setPage(item.id)}>
              <Icon name={item.icon} size={15}/>
              {item.label}
              {item.badge && <span className="badge badge-amber" style={{ marginLeft: "auto", fontSize: 10 }}>{item.badge}</span>}
            </div>
          )
        ))}
      </nav>
      <div style={{ padding: "12px 8px", borderTop: "1px solid var(--border)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px" }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--amber-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "var(--amber)" }}>A</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Admin User</div>
            <div style={{ fontSize: 10, color: "var(--amber)" }}>Super Admin</div>
          </div>
          <button className="btn btn-ghost" style={{ padding: 6, border: "none", background: "transparent" }} onClick={onLogout}>
            <Icon name="logout" size={14} color="var(--text3)"/>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PERFORMANCE PAGE ─────────────────────────────────────────
const PerformancePage = ({ account, trades }) => {
  const closedTrades  = trades.filter(t => t.status === "closed");
  const wins          = closedTrades.filter(t => t.pnl > 0);
  const losses        = closedTrades.filter(t => t.pnl < 0);
  const winRate       = closedTrades.length > 0 ? (wins.length / closedTrades.length) * 100 : 0;
  const avgWin        = wins.length   > 0 ? wins.reduce((a, b) => a + b.pnl, 0)   / wins.length   : 0;
  const avgLoss       = losses.length > 0 ? losses.reduce((a, b) => a + b.pnl, 0) / losses.length : 0;
  const profitFactor  = Math.abs(avgLoss) > 0 ? avgWin / Math.abs(avgLoss) : 0;
  const totalPnl      = closedTrades.reduce((a, b) => a + b.pnl, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <h1 className="page-title">Performance Analytics</h1>
      <div className="grid-4">
        {[
          { label: "Win Rate",       value: `${winRate.toFixed(1)}%`,         sub: `${wins.length}W / ${losses.length}L`,  up: winRate > 50 },
          { label: "Profit Factor",  value: profitFactor.toFixed(2),          sub: "Gross profit / gross loss",            up: profitFactor > 1 },
          { label: "Avg Win",        value: fmt.usd(avgWin),                  sub: "Per winning trade",                    up: true },
          { label: "Avg Loss",       value: fmt.usd(Math.abs(avgLoss)),       sub: "Per losing trade",                     up: false },
        ].map(m => (
          <div key={m.label} className="metric-card">
            <div className="metric-label" style={{ marginBottom: 6 }}>{m.label}</div>
            <div className="metric-value" style={{ fontSize: 22, color: m.up ? "var(--green)" : "var(--red)" }}>{m.value}</div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{m.sub}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>P&L by Symbol</div>
        {["NQ", "ES", "MNQ"].map(sym => {
          const symTrades = closedTrades.filter(t => t.symbol === sym);
          const symPnl    = symTrades.reduce((a, b) => a + b.pnl, 0);
          const pct       = Math.abs(symPnl) / Math.max(1, Math.abs(totalPnl)) * 100;
          return (
            <div key={sym} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>{sym}</span>
                  <span style={{ fontSize: 12, color: "var(--text3)" }}>{symTrades.length} trades</span>
                </div>
                <span className="mono" style={{ color: symPnl >= 0 ? "var(--green)" : "var(--red)" }}>{symPnl >= 0 ? "+" : ""}{fmt.usd(symPnl)}</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${pct}%`, background: symPnl >= 0 ? "var(--green)" : "var(--red)" }}/>
              </div>
            </div>
          );
        })}
      </div>
      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Full Trade History</div>
        <table className="cf-table">
          <thead><tr><th>Symbol</th><th>Side</th><th>Qty</th><th>Entry</th><th>Exit</th><th>P&L</th><th>Time</th></tr></thead>
          <tbody>
            {closedTrades.map(t => (
              <tr key={t.id}>
                <td style={{ fontWeight: 700 }}>{t.symbol}</td>
                <td><span className={`badge ${(t.side === "buy" || t.side === "LONG") ? "badge-green" : "badge-red"}`} style={{ fontSize: 10 }}>{t.side.toUpperCase()}</span></td>
                <td className="mono">{t.qty}</td>
                <td className="mono">{t.entry.toLocaleString()}</td>
                <td className="mono">{t.exit?.toLocaleString()}</td>
                <td className={t.pnl >= 0 ? "num-positive" : "num-negative"}>{t.pnl >= 0 ? "+" : ""}{fmt.usd(t.pnl)}</td>
                <td style={{ color: "var(--text2)" }}>{t.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── DOCUMENTS PAGE ───────────────────────────────────────────
const DocumentsPage = ({ user }) => {
  const docs = [
    { name: "Terms of Service",  status: "signed",  date: "2025-11-15", icon: "file" },
    { name: "Risk Disclosure",   status: "signed",  date: "2025-11-15", icon: "alert" },
    { name: "Trader Agreement",  status: "signed",  date: "2025-11-15", icon: "file" },
    { name: "W-9 Tax Form",      status: "pending", date: null,         icon: "file" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <h1 className="page-title">Documents</h1>
      <div className="card">
        {docs.map((doc, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: i < docs.length - 1 ? "1px solid var(--border)" : "none" }}>
            <div style={{ width: 40, height: 40, borderRadius: "var(--radius)", background: "var(--surface2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name={doc.icon} size={18} color="var(--text2)"/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{doc.name}</div>
              {doc.date && <div style={{ fontSize: 12, color: "var(--text3)" }}>Signed {fmt.date(doc.date)}</div>}
            </div>
            {doc.status === "signed"
              ? <span className="badge badge-green"><Icon name="check" size={11}/> Signed</span>
              : <button className="btn btn-primary btn-sm">Sign Now</button>
            }
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── SUPPORT PAGE ─────────────────────────────────────────────
const SupportPage = () => {
  const [subject, setSubject]     = useState("");
  const [msg, setMsg]             = useState("");
  const [category, setCategory]   = useState("billing");
  const [submitted, setSubmitted] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 640 }} className="fade-in">
      <h1 className="page-title">Support</h1>
      {submitted ? (
        <div className="card" style={{ textAlign: "center", padding: 40 }}>
          <Icon name="check" size={40} color="var(--green)"/>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, marginTop: 16, marginBottom: 8 }}>Ticket Submitted</div>
          <div style={{ color: "var(--text2)" }}>We'll get back to you within 24 hours. Check your email for a confirmation.</div>
          <button className="btn btn-ghost" style={{ marginTop: 20 }} onClick={() => setSubmitted(false)}>Submit Another</button>
        </div>
      ) : (
        <div className="card">
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 16 }}>Submit Support Ticket</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                <option value="billing">Billing & Payments</option>
                <option value="rules">Rules & Violations</option>
                <option value="payouts">Payouts</option>
                <option value="technical">Technical Issues</option>
                <option value="kyc">KYC / Verification</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of your issue"/>
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea rows={5} value={msg} onChange={e => setMsg(e.target.value)} placeholder="Describe your issue in detail..."/>
            </div>
            <button className="btn btn-primary" onClick={() => setSubmitted(true)} disabled={!subject || !msg}>Submit Ticket</button>
          </div>
        </div>
      )}
      <div className="card">
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, marginBottom: 14 }}>Quick Help</div>
        {[
          "How is the trailing drawdown calculated?",
          "When can I request my first payout?",
          "What happens if I breach the daily loss limit?",
          "How do I update my payout method?",
        ].map(q => (
          <div key={q} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
            <Icon name="globe" size={14} color="var(--text3)"/>
            <span style={{ fontSize: 13, color: "var(--text2)" }}>{q}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── ADMIN RISK MONITOR ───────────────────────────────────────
const AdminRisk = () => {
  const risky = [
    { id: "acc_003", trader: "Jordan Lee",  plan: "velocity", size: "150K", drawdownPct: 88, dailyLossPct: 64, status: "warning" },
    { id: "acc_007", trader: "Casey Park",  plan: "core",     size: "100K", drawdownPct: 76, dailyLossPct: 90, status: "critical" },
    { id: "acc_012", trader: "Riley Storm", plan: "velocity", size: "50K",  drawdownPct: 55, dailyLossPct: 45, status: "monitor" },
  ];
  const statusColor = { critical: "var(--red)", warning: "var(--amber)", monitor: "var(--blue)" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="page-title">Risk Monitor</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="dot-pulse"/>
          <span style={{ fontSize: 12, color: "var(--green)" }}>Live monitoring</span>
        </div>
      </div>
      <div className="grid-3">
        {[
          { label: "Accounts Near Breach", value: "3",  color: "var(--amber)" },
          { label: "Active Breaches Today",value: "8",  color: "var(--red)" },
          { label: "Auto-Closed Positions", value: "2", color: "var(--blue)" },
        ].map(m => (
          <div key={m.label} className="metric-card">
            <div className="metric-label" style={{ marginBottom: 6 }}>{m.label}</div>
            <div className="metric-value" style={{ color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", fontFamily: "var(--font-display)", fontWeight: 600 }}>Accounts Near Breach Threshold</div>
        <table className="cf-table">
          <thead><tr><th>Trader</th><th>Plan/Size</th><th>Drawdown Used</th><th>Daily Loss Used</th><th>Risk Level</th><th>Actions</th></tr></thead>
          <tbody>
            {risky.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>{r.trader}</td>
                <td><span style={{ fontSize: 12 }}>{r.plan} · {r.size}</span></td>
                <td>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ width: 80, height: 5, background: "var(--surface2)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${r.drawdownPct}%`, height: "100%", background: r.drawdownPct > 80 ? "var(--red)" : "var(--amber)" }}/>
                    </div>
                    <span className="mono" style={{ fontSize: 11, color: r.drawdownPct > 80 ? "var(--red)" : "var(--amber)" }}>{r.drawdownPct}%</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ width: 80, height: 5, background: "var(--surface2)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${r.dailyLossPct}%`, height: "100%", background: r.dailyLossPct > 80 ? "var(--red)" : "var(--amber)" }}/>
                    </div>
                    <span className="mono" style={{ fontSize: 11, color: r.dailyLossPct > 80 ? "var(--red)" : "var(--amber)" }}>{r.dailyLossPct}%</span>
                  </div>
                </td>
                <td><span className="badge" style={{ background: `${statusColor[r.status]}22`, color: statusColor[r.status] }}>{r.status}</span></td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-ghost btn-xs">View</button>
                    <button className="btn btn-danger btn-xs">Force Close</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── REQUEST RESET PAGE ───────────────────────────────────────
const RequestResetPage = ({ onBack }) => {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_BASE}/auth/request-reset`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      <GlobalStyle/>
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 40, width: "100%", maxWidth: 420 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: "var(--green)", marginBottom: 8 }}>⚡ ClearFund</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Reset Password</h2>
        {sent ? (
          <>
            <p style={{ color: "var(--text2)", fontSize: 14, marginBottom: 24 }}>If an account exists with that email, a reset link has been sent. Check your inbox.</p>
            <button className="btn btn-primary" style={{ width: "100%" }} onClick={onBack}>Back to Login</button>
          </>
        ) : (
          <>
            <p style={{ color: "var(--text2)", fontSize: 14, marginBottom: 24 }}>Enter your email and we'll send you a reset link.</p>
            {error && <div style={{ background: "rgba(255,71,87,0.08)", border: "1px solid rgba(255,71,87,0.25)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--red)", marginBottom: 12 }}>⚠ {error}</div>}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--text2)", display: "block", marginBottom: 6 }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" onKeyDown={e => e.key === 'Enter' && handleSubmit()}/>
            </div>
            <button className="btn btn-primary" style={{ width: "100%", marginBottom: 12 }} onClick={handleSubmit} disabled={loading || !email}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 13, cursor: "pointer", width: "100%" }}>Back to Login</button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── RESET PASSWORD PAGE ──────────────────────────────────────
const ResetPasswordPage = ({ token, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);

  const handleSubmit = async () => {
    if (password !== confirm) return setError('Passwords do not match.');
    if (password.length < 8)  return setError('Password must be at least 8 characters.');
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_BASE}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setDone(true);
      setTimeout(onSuccess, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      <GlobalStyle/>
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 40, maxWidth: 420, textAlign: "center" }}>
        <p style={{ color: "var(--red)" }}>Invalid or missing reset token. Please request a new reset link.</p>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onSuccess}>Go to Login</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      <GlobalStyle/>
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 40, width: "100%", maxWidth: 420 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: "var(--green)", marginBottom: 8 }}>⚡ ClearFund</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Set New Password</h2>
        {done ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <p style={{ color: "var(--text2)" }}>Password updated! Redirecting to login...</p>
          </div>
        ) : (
          <>
            {error && <div style={{ background: "rgba(255,71,87,0.08)", border: "1px solid rgba(255,71,87,0.25)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--red)", marginBottom: 12 }}>⚠ {error}</div>}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--text2)", display: "block", marginBottom: 6 }}>New Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters"/>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 12, color: "var(--text2)", display: "block", marginBottom: 6 }}>Confirm Password</label>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" onKeyDown={e => e.key === 'Enter' && handleSubmit()}/>
            </div>
            <button className="btn btn-primary" style={{ width: "100%" }} onClick={handleSubmit} disabled={loading || !password || !confirm}>
              {loading ? 'Updating...' : 'Set New Password'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── ROOT APP ─────────────────────────────────────────────────
export default function App() {
  return <AppInner />;
}

function AppInner() {
  const storedUser   = auth.getStoredUser();
  const initialView  = (() => {
    if (window.location.pathname === '/reset-password')   return 'reset-password';
    if (window.location.pathname === '/request-reset')    return 'request-reset';
    if (storedUser && !sessionStorage.getItem('scrollToPricing')) {
      return storedUser.role?.toLowerCase() === 'admin' ? 'admin' : 'trader';
    }
    return 'landing';
  })();

  const [view, setView]               = useState(initialView);
  const [resetToken]                  = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [traderPage, setTraderPage]   = useState("dashboard");
  const [adminPage, setAdminPage]     = useState("admin_overview");
  const [showAuth, setShowAuth]       = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [user, setUser]               = useState(storedUser);
  const [toast, setToast]             = useState(null);
  const [realAccount, setRealAccount] = useState(null);
  const [realTrades, setRealTrades]   = useState([]);
  const [legalPage, setLegalPage]     = useState('terms');

  useEffect(() => {
    const onLogout = () => {
      localStorage.removeItem('cf_access_token');
      localStorage.removeItem('cf_refresh_token');
      localStorage.removeItem('cf_user');
      setUser(null); setView("landing"); setTraderPage("dashboard"); setAdminPage("admin_overview");
    };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  useEffect(() => {
    const onReset = () => setView('request-reset');
    window.addEventListener('nav:request-reset', onReset);
    return () => window.removeEventListener('nav:request-reset', onReset);
  }, []);

  useEffect(() => {
    if (user && view === 'trader') {
      setTimeout(async () => {
        try {
          const data = await accounts.list();
          if (data && data.length > 0) {
            const funded     = data.find(a => a.status === 'FUNDED');
            const evaluation = data.find(a => a.status === 'EVALUATION');
            const selected   = funded || evaluation || data[0];
            setRealAccount(selected);
            const tradeData = await tradesApi.list({ accountId: selected.id, status: 'CLOSED', limit: 20 });
            setRealTrades(tradeData?.trades ?? []);
          }
        } catch (err) {
          console.error('Data fetch failed:', err.message);
        }
      }, 200);
    }
  }, [user, view]);

useEffect(() => {
  const onMessage = (e) => {
    if (e.data?.type === 'nav:page') {
      setView('trader');
      setTraderPage(e.data.page);
    }
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}, []);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('purchase') === 'success') {
      showToast('🎉 Purchase successful! Your account is being provisioned.', 'success');
      window.history.replaceState({}, '', '/dashboard');
    }
    if (sessionStorage.getItem('scrollToPricing')) {
      sessionStorage.removeItem('scrollToPricing');
      setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogout = () => {
    localStorage.removeItem('cf_access_token');
    localStorage.removeItem('cf_refresh_token');
    localStorage.removeItem('cf_user');
    setUser(null); setView("landing"); setTraderPage("dashboard"); setAdminPage("admin_overview");
  };

  const handleCheckout = async (plan, size) => {
    if (!user) {
      sessionStorage.setItem('pendingCheckout', JSON.stringify({ plan, size }));
      setShowAuth(true);
      return;
    }
    try {
      const priceId = PRICE_IDS[plan.toLowerCase()][size];
      const data    = await payments.createCheckout({ priceId });
      window.location.href = data.url;
    } catch (err) {
      showToast(err.message || 'Checkout failed. Please try again.', 'error');
    }
  };

  const handleAuth = async (userData) => {
    setUser(userData);
    setShowAuth(false);
    const pending = sessionStorage.getItem('pendingCheckout');
    if (pending) {
      sessionStorage.removeItem('pendingCheckout');
      const { plan, size } = JSON.parse(pending);
      const priceId = PRICE_IDS[plan.toLowerCase()][size];
      try {
        const data = await payments.createCheckout({ priceId });
        window.location.href = data.url;
        return;
      } catch (err) {
        setView(userData.role?.toLowerCase() === "admin" ? "admin" : "trader");
        return;
      }
    }
    if (sessionStorage.getItem('scrollToPricing')) {
      sessionStorage.removeItem('scrollToPricing');
      setView('landing');
      setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 300);
      return;
    }
    setView(userData.role?.toLowerCase() === "admin" ? "admin" : "trader");
  };

  const handleLegal = (page) => { setLegalPage(page); setView('legal'); };

  // ── Early returns (after all hooks) ──────────────────────────────────────
  if (view === 'request-reset') return <RequestResetPage onBack={() => { setView('landing'); setShowAuth(true); }}/>;
  if (view === 'reset-password') return <ResetPasswordPage token={resetToken} onSuccess={() => { setView('landing'); setShowAuth(true); }}/>;

  if (window.location.pathname === '/register') {
    return <><GlobalStyle/><RegisterPage /></>;
  }

  if (window.location.pathname === '/trade') {
  return (
    <TradingTerminal
      account={realAccount || mockAccount}
      onNavigate={(page) => {
        window.opener
          ? window.opener.postMessage({ type: 'nav:page', page }, '*')
          : (window.location.href = `/?page=${page}`);
      }}
      onAccountRefresh={() =>
        accounts.list().then(d => {
          if (d?.length) {
            const funded = d.find(a => a.status === 'FUNDED');
            const evaluation = d.find(a => a.status === 'EVALUATION');
            setRealAccount(funded || evaluation || d[0]);
          }
        }).catch(() => {})
      }
    />
  );
}

  if (view === 'legal') {
    const legalProps = { onBack: () => setView('landing') };
    return (
      <>
        <GlobalStyle/>
        {legalPage === 'terms'   && <TermsPage   {...legalProps}/>}
        {legalPage === 'risk'    && <RiskPage    {...legalProps}/>}
        {legalPage === 'privacy' && <PrivacyPage {...legalProps}/>}
        {legalPage === 'refund'  && <RefundPage  {...legalProps}/>}
        {legalPage === 'kyc_aml' && <KYCAMLPage  {...legalProps}/>}
      </>
    );
  }

  if (view === "landing") return (
    <>
      <GlobalStyle/>
      <LandingPage onLogin={() => setShowAuth(true)} onCheckout={handleCheckout} onUser={user} onDashboard={() => setView(user?.role?.toLowerCase() === 'admin' ? 'admin' : 'trader')} onLegal={handleLegal}/>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onAuth={handleAuth}/>}
      {showCheckout && <CheckoutModal plan="velocity" size="100K" price={229} onClose={() => setShowCheckout(false)} onComplete={() => { setShowCheckout(false); setShowAuth(true); }}/>}
      {toast && <Toast toast={toast}/>}
    </>
  );

  if (view === "admin") return (
    <>
      <GlobalStyle/>
      <div className="cf-app">
        <AdminSidebar page={adminPage} setPage={setAdminPage} onLogout={handleLogout}/>
        <div className="cf-main">
          <div className="cf-topbar">
            <span className="page-title" style={{ fontSize: 16 }}>
              {adminPage === "admin_overview"  && "Platform Overview"}
              {adminPage === "admin_users"     && "User Management"}
              {adminPage === "admin_payouts"   && "Payout Review"}
              {adminPage === "admin_risk"      && "Risk Monitor"}
              {adminPage === "admin_config"    && "Configuration"}
              {adminPage === "admin_accounts"  && "Accounts"}
              {adminPage === "admin_reports"   && "Reports"}
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
              <span className="badge badge-amber">Admin Mode</span>
              <div style={{ fontSize: 12, color: "var(--text2)" }}>{new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
            </div>
          </div>
          <div className="cf-content">
            {adminPage === "admin_overview"  && <AdminOverview/>}
            {adminPage === "admin_users"     && <AdminUsers/>}
            {adminPage === "admin_payouts"   && <AdminPayouts/>}
            {adminPage === "admin_risk"      && <AdminRisk/>}
            {adminPage === "admin_config"    && <AdminConfig/>}
            {(adminPage === "admin_accounts" || adminPage === "admin_reports") && (
              <div className="fade-in">
                <h1 className="page-title" style={{ marginBottom: 16 }}>
                  {adminPage === "admin_accounts" ? "Account Management" : "Analytics & Reports"}
                </h1>
                <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--text2)" }}>
                  <Icon name="chart" size={36} color="var(--text3)"/>
                  <div style={{ marginTop: 12, fontFamily: "var(--font-display)", fontSize: 16 }}>Module in development</div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>This section is included in Phase 2 build.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {toast && <Toast toast={toast}/>}
    </>
  );

  // ── Trader view ────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyle/>
      <div className="cf-app">
        <TraderSidebar page={traderPage} setPage={setTraderPage} user={user || mockUser} onLogout={handleLogout} account={realAccount || mockAccount}/>
        <div className="cf-main">
          {traderPage !== "trading" && (
            <div className="cf-topbar">
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 15, textTransform: "capitalize" }}>{traderPage}</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
                <span className="mono" style={{ fontSize: 12, color: "var(--green)" }}>Balance: {fmt.usd(Number((realAccount || mockAccount).balance))}</span>
                <span className="badge badge-green"><span className="dot-pulse" style={{ width: 6, height: 6, background: "currentColor" }}/> Funded</span>
              </div>
            </div>
          )}
          <div className={traderPage === "trading" ? "" : "cf-content"} style={traderPage === "trading" ? { flex: 1, overflow: "hidden" } : {}}>
            {traderPage === "dashboard"   && <DashboardPage   account={realAccount || mockAccount} trades={realTrades.length ? realTrades : mockTrades}/>}
            {traderPage === "trading"     && <TradingPage     account={realAccount || mockAccount} onAccountRefresh={() => accounts.list().then(d => { if (d?.length) { const funded = d.find(a => a.status === 'FUNDED'); const evaluation = d.find(a => a.status === 'EVALUATION'); setRealAccount(funded || evaluation || d[0]); } }).catch(() => {})}/>}
            {traderPage === "performance" && <PerformancePage account={realAccount || mockAccount} trades={realTrades.length ? realTrades : mockTrades}/>}
            {traderPage === "payouts"     && <PayoutsPage     account={realAccount || mockAccount} user={user || mockUser}/>}
            {traderPage === "kyc"         && <KYCPage         user={user || mockUser}/>}
            {traderPage === "documents"   && <DocumentsPage   user={user || mockUser}/>}
            {traderPage === "support"     && <SupportPage/>}
          </div>
        </div>
      </div>
      {toast && <Toast toast={toast}/>}
    </>
  );
}
